import React, { useEffect, useState, useRef, useMemo } from 'react';
import socketIOClient from "socket.io-client";

import { useLocation, useNavigate } from 'react-router-dom';

import { useSelector, useDispatch } from 'react-redux';
import { fetchUserJobs, fetchNewJobs, checkJobSyncStatus, setSyncing, fetchRecruiterForJob, updateJobsWithScores, clearScrollTargetJobId } from '../../../redux/JobSlice/jobSlice';
import { fetchCompanies } from '../../../redux/CompanySlice/companySlice';
import { US_CITY_OPTIONS } from '../../../constants/usCities';
import { fetchUserProfile } from '../../../redux/AuthSlice/authSlice';
import { fetchSavedJobs, saveMultipleJobs } from '../../../redux/SavedJobSlice/savedJobSlice';
import { fetchAllContactsByCompanyId, fetchSignalsByCompany, setSearchComplete, startRealtimeSearch, saveRecruiterContacts, setCompanyAnalysisComplete, generateReferralStrategy } from '../../../redux/ContactSlice/contactSlice';

import api from '../../../api/axios';
import enhancedApi from '../../../utils/enhancedApi';
import OnboardingModal from '../../../components/Modals/OnboardingModal';
import FetchJobsModal from '../../../components/Modals/FetchJobsModal';
import { performanceMonitor, jobPerformance } from '../../../utils/performanceMonitor';
import { useDebounce, useThrottle } from '../../../hooks/useOptimizations';
import JobSidebar from '../../../components/JobsPage/JobSidebar/JobSidebar';
import JobHeader from '../../../components/JobsPage/JobHeader/JobHeader';
import JobSubHeader from '../../../components/JobsPage/JobHeader/JobSubHeader';
import JobCard from '../../../components/JobsPage/JobContent/JobCard';
import JobCompanyProfile from '../../../components/JobsPage/JobContent/JobCompanyProfile';
import CompanyInsightsPanel from '../../../components/JobsPage/JobContent/CompanyInsightsPanel';
import Navbaar from '../../../components/Navbar/Navbaar';
import Sidebar from '../../../components/Navbar/SideNavbar';
import LoadingOverlay from '../../../components/common/LoadingOverlay';
import JobsFetchingLoader from '../../../components/common/JobsFetchingLoader';
import ScanProgressPill from '../../../components/common/ScanProgressPill';
import ErrorBoundary from '../../../components/common/ErrorBoundary';
import RecruiterSearchOverlayInline from '../../../components/common/RecruiterSearchOverlayInline';
import JobNotification from '../../../components/common/JobNotification';
import RecruiterModal from '../../../components/JobsPage/JobContent/RecruiterModal';
import RightPanelTabs from '../../../components/JobsPage/RightPanelTabs';

// Lets the progress pill survive a reload. The fetch is an awaited request, so a
// refresh kills the client half — but the server finishes the work and saves the
// jobs regardless. Without this the user reloads and the app looks idle while
// jobs are still landing.
const JOBS_FETCH_KEY = 'cc_jobs_fetch_in_progress';
// Backstop only. The server's own run record is the real completion signal now,
// so this fires solely when that record is unreachable or never terminal.
const JOBS_FETCH_TTL_MS = 8 * 60 * 1000;
const JOBS_FETCH_POLL_MS = 4000;

// Skeleton placeholder for a job card while jobs load.
const JobCardSkeleton = () => (
    <div className="w-full bg-white rounded-2xl border border-[#E6E8EE] p-5 animate-pulse" style={{ boxShadow: '0 1px 3px rgba(15,23,42,0.06)' }}>
        <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#EEF0F4] shrink-0" />
            <div className="flex-1 min-w-0 space-y-2.5">
                <div className="h-4 w-2/3 bg-[#EAECF2] rounded" />
                <div className="h-3 w-1/3 bg-[#F0F1F5] rounded" />
                <div className="flex gap-2 mt-1">
                    <div className="h-5 w-16 bg-[#F0F1F5] rounded-full" />
                    <div className="h-5 w-20 bg-[#F0F1F5] rounded-full" />
                </div>
            </div>
            <div className="w-12 h-12 rounded-full bg-[#EEF0F4] shrink-0" />
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-4">
            {[0, 1, 2, 3, 4, 5].map(i => <div key={i} className="h-12 bg-[#F4F5F8] rounded-lg" />)}
        </div>
        <div className="flex gap-2 mt-4">
            <div className="h-9 w-32 bg-[#EAECF2] rounded-lg" />
            <div className="h-9 w-28 bg-[#F0F1F5] rounded-lg" />
        </div>
    </div>
);

const Jobs = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {
        enrichedJobsArray,
        totalJobs,
        loading: jobsLoading,
        loadingMoreJobs,
        syncing,
        lastSyncedAt,
        error: jobsError,
        recruitersByJobId,
        scrollTargetJobId,
    } = useSelector((state) => state.jobs);

    const { companies } = useSelector((state) => state.companies);
    const savedJobs = useSelector((state) => state.savedJobs.savedJobs);
    const user = useSelector((state) => state.auth.user);
    const referralStrategies = useSelector((state) => state.contacts.referralStrategies || {});
    const effectiveUserId = user?._id || user?.id;

    // The full-screen "Fetching jobs for you…" overlay should only appear the
    // FIRST time a user lands on Jobs. After that we suppress it (jobs still load
    // in the background) so returning users aren't blocked on every login.
    const jobsSeenKey = effectiveUserId ? `cc_jobs_seen_${effectiveUserId}` : null;
    const isFirstJobsVisit = !jobsSeenKey || (typeof window !== 'undefined' && !localStorage.getItem(jobsSeenKey));

    const prevSyncing = useRef(syncing);

    const [showModal, setShowModal] = useState(false);
    const [showFetchJobsModal, setShowFetchJobsModal] = useState(false);
    const [jobProfileLoading, setJobProfileLoading] = useState(false);

    // Filters survive leaving the list (e.g. opening a job detail and coming
    // back): this component unmounts on navigation, so state alone is lost.
    // sessionStorage keeps them for the browser-tab session; a fresh tab starts clean.
    const persistedFilters = useMemo(() => {
        try { return JSON.parse(sessionStorage.getItem('cc_jobs_filters')) || {}; }
        catch { return {}; }
    }, []);
    const [query, setQuery] = useState(persistedFilters.query || '');
    const [debouncedQuery, setDebouncedQuery] = useState(query);
    const [selectedJobTypes, setSelectedJobTypes] = useState(persistedFilters.jobTypes || []);
    const [minSalary, setMinSalary] = useState(persistedFilters.minSalary ?? 0);
    const [maxSalary, setMaxSalary] = useState(persistedFilters.maxSalary ?? 1000000);
    const [selectedExperience, setSelectedExperience] = useState(persistedFilters.experience || []);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedJobs, setSelectedJobs] = useState([]);
    const [toastMessage, setToastMessage] = useState({ show: false, message: '', type: 'success' });
    const [localLoading, setLocalLoading] = useState(false);
    const [keywordFetchLoading, setKeywordFetchLoading] = useState(false);
    const [atsFetchLoading, setAtsFetchLoading] = useState(false);
    // A job fetch can run for a while. Nothing about it requires the UI to be
    // blocked, so the user can send the loader to a corner pill and keep working.
    const [fetchMinimised, setFetchMinimised] = useState(false);
    // A fetch that was interrupted by a reload. Shown as a pill only, never as the
    // blocking loader — there is no request left on this page to block on.
    const [restoredFetch, setRestoredFetch] = useState(false);
    // When the running fetch began. Owned by the page, not the loader, so progress
    // is continuous across minimise/expand and across a reload.
    const [fetchStartedAt, setFetchStartedAt] = useState(null);
    const [jobsidebarOpen, setJobsidebarOpen] = useState(false);
    const [locationQuery, setLocationQuery] = useState('');
    const [selectedCities, setSelectedCities] = useState(persistedFilters.cities || []);
    // Multi-select match-score ranges; empty array = all scores.
    const [selectedScoreRanges, setSelectedScoreRanges] = useState(persistedFilters.scoreRanges || []);
    const [sortBy, setSortBy] = useState(persistedFilters.sortBy || 'date');

    useEffect(() => {
        try {
            sessionStorage.setItem('cc_jobs_filters', JSON.stringify({
                query,
                jobTypes: selectedJobTypes,
                minSalary,
                maxSalary,
                experience: selectedExperience,
                cities: selectedCities,
                scoreRanges: selectedScoreRanges,
                sortBy,
            }));
        } catch { /* storage full/unavailable — filters just won't persist */ }
    }, [query, selectedJobTypes, minSalary, maxSalary, selectedExperience, selectedCities, selectedScoreRanges, sortBy]);

    const [initialJobsFetched, setInitialJobsFetched] = useState(false);
    const [showJobNotification, setShowJobNotification] = useState(false);
    const [jobsLoadedCount, setJobsLoadedCount] = useState(0);
    const prevJobsCount = useRef(0);
    const isInitialLoad = useRef(true);
    const consumedScrollTargetJobIdRef = useRef('');
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [jobsPerPage, setJobsPerPage] = useState(20);

    // Quick-filter chips
    const [quickFilter, setQuickFilter] = useState('all');

    // "Last fetched" = exactly the jobs returned by the MOST RECENT fetch action.
    // We track their IDs explicitly (persisted to localStorage so it survives refresh)
    // instead of guessing batches from addedAt time-gaps — multiple fetches done close
    // together used to merge into one batch, which over-counted "Last fetched".
    const LAST_FETCHED_KEY = 'cc:lastFetchedJobIds';
    const [lastFetchedIds, setLastFetchedIds] = useState(() => {
        try {
            const raw = JSON.parse(localStorage.getItem(LAST_FETCHED_KEY) || '[]');
            return new Set((Array.isArray(raw) ? raw : []).map(String));
        } catch {
            return new Set();
        }
    });

    // After a fetch, record ONLY this fetch's job IDs (overwriting the previous batch)
    // and auto-focus them via the "Last fetched" filter.
    const markRecentlyFetched = (ids) => {
        const clean = (Array.isArray(ids) ? ids : []).filter(Boolean).map(String);
        if (clean.length > 0) {
            setLastFetchedIds(new Set(clean));
            try { localStorage.setItem(LAST_FETCHED_KEY, JSON.stringify(clean)); } catch { /* ignore */ }
            setQuickFilter('last-fetched');
        }
    };

    // Save jobs loading state
    const [savingJobs, setSavingJobs] = useState(false);
    const [saveJobsProgress, setSaveJobsProgress] = useState("Saving jobs...");
    
    // Recruiter loading state (non-blocking)
    const [loadingJobId, setLoadingJobId] = useState(null);
    const [loadingCompanyName, setLoadingCompanyName] = useState(null);
    const centerJobsListRef = useRef(null);
    const [chatScrollHighlightKey, setChatScrollHighlightKey] = useState('');
    const consumedChatTargetRef = useRef('');

    const getStableId = (value) => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'string' || typeof value === 'number') return String(value);
        if (typeof value !== 'object') return '';
        if (value.$oid) return String(value.$oid);
        if (value._id) return getStableId(value._id);
        return '';
    };

    const getJobCompanyIdValue = (job) => {
        if (!job?.companyId) return '';
        if (typeof job.companyId === 'string') return job.companyId;
        if (typeof job.companyId === 'object') {
            if (job.companyId._id) return getStableId(job.companyId._id) || String(job.companyId._id);
            if (job.companyId.$oid) return String(job.companyId.$oid);
        }
        return '';
    };

    const normalizeMatchText = (value) => (value || '').toString().trim().toLowerCase();

    const getJobMatchSignature = (job) => {
        const id = getStableId(job?._id || job?.id || job?.jobId);
        if (id) return `id:${id}`;
        const title = normalizeMatchText(job?.title || job?.jobPosition || job?.job_title);
        const company = normalizeMatchText(job?.company || job?.companyName || job?.companyId?.name);
        const location = normalizeMatchText(job?.location || job?.jobLocation);
        return `meta:${title}::${company}::${location}`;
    };

    const doesJobMatchChatTarget = (job, target) => {
        if (!job || !target) return false;
        const targetId = getStableId(target.id || target._id || target.jobId);
        const jobId = getStableId(job._id || job.id || job.jobId);
        if (targetId && jobId && targetId === jobId) return true;

        const targetTitle = normalizeMatchText(target.title || target.jobPosition || target.job_title);
        const targetCompany = normalizeMatchText(target.company || target.companyName);
        const targetLocation = normalizeMatchText(target.location || target.jobLocation);

        const jobTitle = normalizeMatchText(job.title || job.jobPosition || job.job_title);
        const jobCompany = normalizeMatchText(job.company || job.companyName || job.companyId?.name);
        const jobLocation = normalizeMatchText(job.location || job.jobLocation);

        if (!targetTitle || !targetCompany) return false;
        const titleMatch = jobTitle === targetTitle;
        const companyMatch = jobCompany === targetCompany;
        if (!titleMatch || !companyMatch) return false;
        if (!targetLocation) return true;
        return jobLocation === targetLocation;
    };


    
    // 🆕 NEW STATE for company cards system
    const [fetchedCompanies, setFetchedCompanies] = useState([]);
    const [activeRightTab, setActiveRightTab] = useState('recruiters');
    const [activeCompanyModal, setActiveCompanyModal] = useState(null);
    const [isRecruiterModalOpen, setIsRecruiterModalOpen] = useState(false);
    const processingCompaniesRef = useRef(new Set()); // Track companies being processed
    const requestedStrategiesRef = useRef(new Set()); // Avoid duplicate strategy requests per org
    const referralCardsLoadedRef = useRef(false);
    const persistedReferralCardsRef = useRef('');
    const RECRUITER_CARDS_STORAGE_PREFIX = 'cc_recruiter_cards_session';
    const recruiterCardsStorageKey = useMemo(
        () => effectiveUserId ? `${RECRUITER_CARDS_STORAGE_PREFIX}:${effectiveUserId}` : null,
        [effectiveUserId]
    );

    const buildPersistableReferralCards = (cards) => cards
        .filter((card) => card?.jobId && card?.companyKey && card?.status !== 'loading')
        .map((card) => ({
            companyKey: card.companyKey,
            jobId: getStableId(card.jobId),
            jobData: card.jobData || null,
            organizationId: card.organizationId || null,
            firstFetchedAtMs: card.firstFetchedAtMs || card.createdAtMs || Date.now(),
            recruiterData: card.recruiterData || null,
            analyzedData: card.analyzedData || null,
            fetchFailed: Boolean(card.fetchFailed),
            errorMessage: card.errorMessage || null,
            analysisComplete: Boolean(card.analysisComplete),
            strategyComplete: Boolean(card.strategyComplete),
            status: card.status || 'loading',
            createdAt: card.createdAt || Date.now()
        }));

    useEffect(() => {
        referralCardsLoadedRef.current = false;
        persistedReferralCardsRef.current = '';
    }, [recruiterCardsStorageKey]);

    // Load cards from sessionStorage on mount (persists across same-browser login sessions)
    useEffect(() => {
        if (!recruiterCardsStorageKey) {
            setFetchedCompanies([]);
            return;
        }

        const stored = sessionStorage.getItem(recruiterCardsStorageKey);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                console.log('📦 [Jobs] Restored', parsed.length, 'cards from session storage');
                
                // Only restore completed cards (filter out stuck loading/analyzing states)
                const validCards = sortReferralCards(parsed)
                    .filter(card => card.status === 'complete')
                    .map(card => ({
                        ...card,
                        analysisComplete: true,
                        strategyComplete: true
                    }));
                
                setFetchedCompanies(validCards);
                console.log('✅ [Jobs] Restored', validCards.length, 'completed cards');
            } catch (e) {
                console.warn('⚠️ Failed to parse session storage:', e);
            }
        }
    }, [recruiterCardsStorageKey]);

    // Save cards to sessionStorage whenever they change
    useEffect(() => {
        if (!recruiterCardsStorageKey) return;

        console.log('📊 [Jobs] fetchedCompanies state changed:');
        console.log('   Total cards:', fetchedCompanies.length);
        console.log('   Cards:', fetchedCompanies.map(c => ({
            key: c.companyKey,
            status: c.status,
            company: c.jobData?.companyName
        })));
        
        if (fetchedCompanies.length > 0) {
            sessionStorage.setItem(recruiterCardsStorageKey, JSON.stringify(fetchedCompanies));
            console.log('💾 [Jobs] Saved', fetchedCompanies.length, 'cards to session storage');
        } else {
            sessionStorage.removeItem(recruiterCardsStorageKey);
            console.log('🗑️ [Jobs] Cleared session storage (no cards)');
        }
    }, [fetchedCompanies, recruiterCardsStorageKey]);

    useEffect(() => {
        if (!recruiterCardsStorageKey || !effectiveUserId || enrichedJobsArray.length === 0 || referralCardsLoadedRef.current) {
            return;
        }

        let cancelled = false;
        referralCardsLoadedRef.current = true;

        const loadPersistedReferralCards = async () => {
            try {
                const response = await api.get('/api/recruitercontacts/job-referral-analyses');
                if (response.data?.persistenceAvailable === false) {
                    console.info('[Jobs] Referral analysis history unavailable; using session fallback.');
                    return;
                }
                const persistedCards = sortReferralCards(Array.isArray(response.data?.cards) ? response.data.cards : []);
                if (cancelled || persistedCards.length === 0) return;

                setFetchedCompanies((prev) => {
                    const existingJobIds = new Set(prev.map((card) => getStableId(card.jobId)));
                    const restoredCards = persistedCards.filter((card) => !existingJobIds.has(getStableId(card.jobId)));
                    return restoredCards.length ? sortReferralCards([...prev, ...restoredCards]) : prev;
                });
            } catch (error) {
                console.warn('[Jobs] Failed to load persisted referral analyses:', error?.message || error);
            }
        };

        loadPersistedReferralCards();

        return () => {
            cancelled = true;
        };
    }, [recruiterCardsStorageKey, effectiveUserId, enrichedJobsArray.length]);

    useEffect(() => {
        if (!recruiterCardsStorageKey || !effectiveUserId || fetchedCompanies.length === 0) {
            return;
        }

        const cards = buildPersistableReferralCards(fetchedCompanies);
        const serialized = JSON.stringify(cards);
        if (cards.length === 0 || serialized === persistedReferralCardsRef.current) {
            return;
        }

        const timeoutId = setTimeout(async () => {
            try {
                const response = await api.post('/api/recruitercontacts/job-referral-analyses', { cards });
                if (response.data?.persistenceAvailable === false) {
                    console.info('[Jobs] Referral analysis saved for this session; backend history is temporarily unavailable.');
                    return;
                }
                persistedReferralCardsRef.current = serialized;
            } catch (error) {
                console.warn('[Jobs] Failed to persist referral analyses:', error?.message || error);
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [fetchedCompanies, recruiterCardsStorageKey, effectiveUserId]);

    useEffect(() => {
        if (!loadingJobId) return;

        const matchingCard = fetchedCompanies.find((card) => String(card?.jobId || '') === String(loadingJobId));
        if (!matchingCard) return;

        const recruiterSearchFinished =
            matchingCard.fetchFailed ||
            matchingCard.status === 'failed' ||
            matchingCard.status === 'complete' ||
            Boolean(matchingCard.recruiterData) ||
            Boolean(matchingCard.analysisComplete);

        if (recruiterSearchFinished) {
            setLoadingJobId(null);
            setLoadingCompanyName(null);
        }
    }, [loadingJobId, fetchedCompanies]);

    useEffect(() => {
        setFetchedCompanies(prev => {
            let changed = false;
            const updated = prev.map(card => {
                if (!card.organizationId || card.strategyComplete) {
                    return card;
                }
                const entry = referralStrategies[card.organizationId];
                if (!entry || entry.loading) {
                    return card;
                }
                if (!entry.data && !entry.error) {
                    return card;
                }
                changed = true;
                const nextCard = { ...card, strategyComplete: true };
                nextCard.status = computeCardStatus(nextCard);
                if (nextCard.status === 'complete') {
                    processingCompaniesRef.current.delete(nextCard.companyKey);
                }
                return nextCard;
            });
            return changed ? updated : prev;
        });
    }, [referralStrategies]);

    // Helper: derive a stable key per company so multiple jobs from the same
    // company collapse into a single card. Preference order:
    // 1. job._id (most reliable unique identifier)
    // 2. companyName::jobTitle combo (fallback for grouping)
    const getCompanyKey = (job) => {
        if (!job) return '';
        return job._id || `${(job.companyName || job.company || 'UNKNOWN').toLowerCase()}::${(job.jobTitle || job.jobPosition || job.title || '').toLowerCase()}`;
    };

    const getReferralCardOrder = (card) => Number(
        card?.firstFetchedAtMs ||
        card?.createdAtMs ||
        card?.createdAt ||
        0
    );

    const sortReferralCards = (cards) => [...cards].sort((left, right) => getReferralCardOrder(left) - getReferralCardOrder(right));

    useEffect(() => {
        if (!recruiterCardsStorageKey || fetchedCompanies.length === 0 || enrichedJobsArray.length === 0) {
            return;
        }

        const activeJobIds = new Set(
            enrichedJobsArray
                .map((job) => getStableId(job?._id || job?.id || job?.jobId))
                .filter(Boolean)
        );
        const activeCompanyKeys = new Set(
            enrichedJobsArray
                .map((job) => getCompanyKey(job))
                .filter(Boolean)
        );
        const activeJobSignatures = new Set(
            enrichedJobsArray
                .map((job) => getJobMatchSignature(job))
                .filter(Boolean)
        );

        const filtered = fetchedCompanies.filter((card) => {
            const cardJobId = getStableId(card?.jobId || card?.jobData?._id || card?.jobData?.id || card?.jobData?.jobId);
            if (cardJobId && activeJobIds.has(cardJobId)) return true;

            const cardCompanyKey = card?.companyKey || getCompanyKey(card?.jobData);
            if (cardCompanyKey && activeCompanyKeys.has(cardCompanyKey)) return true;

            const cardSignature = getJobMatchSignature(card?.jobData);
            return cardSignature && activeJobSignatures.has(cardSignature);
        });

        if (filtered.length === fetchedCompanies.length) return;

        const filteredJobIds = new Set(filtered.map((card) => getStableId(card?.jobId)).filter(Boolean));
        const removedJobIds = fetchedCompanies
            .map((card) => getStableId(card?.jobId || card?.jobData?._id || card?.jobData?.jobId))
            .filter((jobId) => jobId && !filteredJobIds.has(jobId));

        setFetchedCompanies(filtered);
        removedJobIds.forEach((jobId) => {
            api.delete(`/api/recruitercontacts/job-referral-analyses/${jobId}`).catch((error) => {
                console.warn('[Jobs] Failed to delete stale referral analysis:', error?.message || error);
            });
        });
    }, [recruiterCardsStorageKey, fetchedCompanies, enrichedJobsArray]);

    const deriveOrganizationId = (job, recruiterData = null) => {
        if (job?.organizationId) return job.organizationId;
        if (job?.organization_id) return job.organization_id;
        if (job?.companyId?._id) return job.companyId._id;
        if (job?.companyId) return job.companyId;
        if (job?.company?.organizationId) return job.company.organizationId;
        if (job?.company?._id) return job.company._id;
        if (job?._id) return job._id;

        if (recruiterData) {
            const sample = recruiterData.hiring_manager?.best_guesses?.[0] || recruiterData.recruiters?.best_guesses?.[0];
            if (sample) {
                return (
                    sample.organization_id ||
                    sample._fullData?.organization_id ||
                    sample._fullData?.organization?.linkedin_company_id ||
                    sample._fullData?.organization?.id
                );
            }
        }
        return null;
    };

    const buildContactPayload = (contact) => ({
        name: contact.name,
        title: contact.title,
        department: contact.department || contact._fullData?.department,
        location: contact.location,
        overall_experience_years: contact._fullData?.overall_experience_years,
        company_experience_years: contact._fullData?.company_experience_years,
        summary: contact._fullData?.summary,
        skills: contact._fullData?.skills,
        education: contact._fullData?.education,
        certifications: contact._fullData?.certifications,
        email: contact.email,
        profile_url: contact.profile_url,
        followers: contact._fullData?.followers,
        confidence_score: contact.confidence_score,
        referral_probability: contact.referral_probability,
        data_source: contact._fullData?.data_source,
        _fullData: contact._fullData
    });

    const computeCardStatus = (card) => {
        if (card.fetchFailed) {
            return 'failed';
        }
        if (card.analysisComplete && card.strategyComplete) {
            return 'complete';
        }
        if (card.recruiterData) {
            return 'analyzing';
        }
        return 'loading';
    };

    const requestStrategyForOrg = async ({ orgId, jobData, contacts }) => {
        if (!orgId || !jobData || !contacts?.length) {
            return;
        }
        const existingEntry = referralStrategies[orgId];
        if (existingEntry?.data || existingEntry?.loading) {
            console.log('ℹ️ [Jobs] Strategy already available or loading for org:', orgId);
            return;
        }
        if (requestedStrategiesRef.current.has(orgId)) {
            console.log('ℹ️ [Jobs] Strategy request already queued for org:', orgId);
            return;
        }
        requestedStrategiesRef.current.add(orgId);

        const jobDetails = {
            companyName: jobData.companyName,
            jobPosition: jobData.jobPosition,
            jobLocation: jobData.jobLocation,
            jobDesc: jobData.jobDesc,
            job_department_if_any: jobData.department || null
        };

        const userProfile = user ? {
            name: user.name,
            jobTitle: user.jobTitle || user.currentTitle,
            currentTitle: user.currentTitle,
            experience: user.experience,
            skills: user.skills || [],
            education: user.education || [],
            experience_details: user.experience || [],
            resume: user.resume || '',
            preferredDepartment: user.preferredDepartment || [],
            preferredIndustry: user.preferredIndustry || [],
            previousIndustryYears: user.previousIndustryYears || []
        } : {};

        try {
            console.log('🎯 [Jobs] Requesting referral strategy for org:', orgId);
            await dispatch(generateReferralStrategy({
                jobId: jobData.id || jobData._id,
                organizationId: orgId,
                jobDetails,
                userProfile,
                allContacts: contacts.map(buildContactPayload)
            })).unwrap();
            console.log('✅ [Jobs] Strategy request finished for org:', orgId);
        } catch (error) {
            console.error('❌ [Jobs] Failed to generate referral strategy for org:', orgId, error);
        } finally {
            requestedStrategiesRef.current.delete(orgId);
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedQuery(query);
        }, 300);

        return () => {
            clearTimeout(handler);
        };
    }, [query]);

    useEffect(() => {
        dispatch(fetchUserProfile());
        dispatch(fetchCompanies({ forceRefresh: true })).then((result) => {
            console.log("🏢 Companies fetch result:", result);
            console.log("📊 Companies received:", result.payload?.length || 0);
        });
    }, [dispatch]);

    useEffect(() => {
        if (!companies || companies.length === 0 || enrichedJobsArray.length === 0) return;
        dispatch(updateJobsWithScores({ companies }));
    }, [dispatch, companies, enrichedJobsArray.length]);

    // When jobs have pending scores (background calculation), re-fetch companies after a delay
    // so we pick up the newly-calculated scores without a manual refresh.
    useEffect(() => {
        const hasPendingScores = enrichedJobsArray.some(j => j.scoresStatus === 'pending');
        if (!hasPendingScores) return;

        const timer = setTimeout(() => {
            console.log('🔄 [Jobs] Detected pending scores — refreshing company data...');
            dispatch(fetchCompanies({ forceRefresh: true }));
        }, 15000); // 15 s — enough for the background scoring to finish

        return () => clearTimeout(timer);
    }, [dispatch, enrichedJobsArray]);
    
    // Keep the loaded-count baseline in sync. The "N new jobs loaded" popup is shown
    // explicitly by the fetch handlers with the REAL added count — deriving it here from
    // the change in loaded-list length fired spuriously (e.g. "10 new jobs loaded" when a
    // reload merely refilled the capped list and nothing was actually fetched).
    useEffect(() => {
        if (!jobsLoading && enrichedJobsArray.length > 0) {
            prevJobsCount.current = enrichedJobsArray.length;
            if (isInitialLoad.current) {
                isInitialLoad.current = false;
            }
        }
    }, [jobsLoading, enrichedJobsArray]);

    const socketRef = useRef(null);

    useEffect(() => {
        if (!syncing) {
            return;
        }

        console.log("Syncing is true, starting to poll for status...");

        const intervalId = setInterval(async () => {
            try {
                const statusResult = await dispatch(checkJobSyncStatus()).unwrap();
                console.log(statusResult)

                if (!statusResult.isSyncing) {
                    console.log("✅ Sync complete! Stopping poll and fetching results.");
                    clearInterval(intervalId);
                    dispatch(setSyncing(false));
                    dispatch(fetchUserJobs());
                } else {
                    console.log("...still syncing, will check again in 10s.");
                }
            } catch (error) {
                console.error("Polling failed, stopping.", error);
                clearInterval(intervalId);
                dispatch(setSyncing(false));
            }
        }, 10000); // Poll every 10 seconds

        return () => clearInterval(intervalId);

    }, [syncing, dispatch]);


    useEffect(() => {
        if (effectiveUserId) {
            dispatch(fetchSavedJobs(effectiveUserId));
        }
    }, [dispatch, effectiveUserId]);

    useEffect(() => {
        if (location.state?.showOnboardingModal) {
            setShowModal(true);
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    useEffect(() => {
        if (!effectiveUserId || initialJobsFetched) return;

        // If jobs already exist in Redux (e.g. navigated from AI assistant),
        // avoid re-fetching and skip the full-screen "Fetching jobs" experience.
        if (Array.isArray(enrichedJobsArray) && enrichedJobsArray.length > 0) {
            setInitialJobsFetched(true);
            return;
        }

        let cancelled = false;
        console.log("🚀 Fetching initial jobs...");
        dispatch(fetchUserJobs())
            .then((result) => {
                if (cancelled) return;
                console.log("📥 Jobs fetch result:", result);
                console.log("📊 Jobs received:", result.payload?.length ?? 0);
            })
            .finally(() => {
                setInitialJobsFetched(true);
            });

        return () => {
            cancelled = true;
        };
    }, [dispatch, effectiveUserId, initialJobsFetched, enrichedJobsArray.length]);

    // Mark the first-visit overlay as "seen" once the initial load completes, so
    // it never blocks this user again on subsequent logins.
    useEffect(() => {
        if (effectiveUserId && initialJobsFetched && !jobsLoading && !syncing) {
            try { localStorage.setItem(`cc_jobs_seen_${effectiveUserId}`, '1'); } catch {}
        }
    }, [effectiveUserId, initialJobsFetched, jobsLoading, syncing]);

    // Debug: Log enrichedJobsArray changes
    useEffect(() => {
        console.log("📋 enrichedJobsArray updated:", {
            length: enrichedJobsArray.length,
            loading: jobsLoading,
            sample: enrichedJobsArray.slice(0, 2)
        });
    }, [enrichedJobsArray, jobsLoading]);

    useEffect(() => {
        if (!toastMessage.show) return undefined;
        const timer = setTimeout(() => {
            setToastMessage({ show: false, message: '', type: 'success' });
        }, 3000);
        return () => clearTimeout(timer);
    }, [toastMessage]);

    const handleCloseModal = () => {
        setShowModal(false);
        window.history.replaceState({}, document.title);
    };

    // Strip ALL dash variants (-, –, —), underscores, and whitespace: job data
    // contains 'Full–time' (en-dash) and 'full_time' alongside 'Full-time'.
    const normalize = (str) => str?.toLowerCase().replace(/[-–—_\s]/g, '');

    // Stored job.experienceLevel vocabulary is Entry/Junior/Mid/Senior/Executive —
    // it does NOT contain the filter-chip terms (midsenior, director…), so each chip
    // maps to the stored values it covers. Buckets overlap on purpose (OR semantics).
    const EXPERIENCE_LEVEL_BUCKETS = {
        newgraduate: ['junior'],
        entrylevel: ['entry', 'junior'],
        associate: ['mid'],
        midsenior: ['mid', 'senior'],
        director: ['senior', 'executive'],
        executive: ['executive'],
    };

    const uniqueCities = [...new Set(enrichedJobsArray.map(job => job.jobLocation))].filter(Boolean);

    // Locations actually present on loaded jobs (exact strings, incl. international)…
    let cityOptions = uniqueCities.map(city => ({
        value: city,
        label: city,
    }));

    // …merged with all major US cities, so users can filter by their own city even
    // before a matching job is loaded. Location matching is substring-based, so the
    // bare city-name values here match any "City, ST" / "City, State" job location.
    const seenCityLabels = new Set(cityOptions.map(o => o.label.toLowerCase()));
    for (const opt of US_CITY_OPTIONS) {
        if (!seenCityLabels.has(opt.label.toLowerCase())) {
            cityOptions.push(opt);
            seenCityLabels.add(opt.label.toLowerCase());
        }
    }
    cityOptions.sort((a, b) => a.label.localeCompare(b.label));

    cityOptions = [
        { value: "remote", label: "Remote" },
        ...cityOptions,
    ];

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedQuery, selectedJobTypes, minSalary, maxSalary, selectedExperience, selectedCities, selectedScoreRanges, sortBy, quickFilter]);
    
    // Scroll to top when page changes
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentPage]);
    
    const baseFilteredJobs = useMemo(() => {
        return enrichedJobsArray.filter((job) => {
            const q = debouncedQuery.toLowerCase().trim();
            const companyName = (job.companyName || job.company || job.companyId?.name || '').toLowerCase();
            const jobPosition = (job.jobPosition || job.title || '').toLowerCase();
            const matchesSearch = !q || jobPosition.includes(q) || companyName.includes(q);

            // Substring match, not equality: stored jobType includes compounds like
            // "Full-time and Contractor". "Remote" is a location/policy, not a type;
            // "Contract" covers Contract / Contractor / "…and Contractor".
            const jobTypeNormalized = normalize(job.jobType) || '';
            const isRemoteJob =
                (job.jobLocation || '').toLowerCase().includes('remote') ||
                (job.remote_policy || '').toLowerCase().includes('remote');
            // Unknown jobType passes (consistent with unscored jobs passing the score
            // filter): unlabeled data is "unknown", not a mismatch. Remote is judged
            // from location/policy, so it ignores the missing-type bypass.
            const matchesType = selectedJobTypes.length === 0 || selectedJobTypes.some(t =>
                t === 'remote' ? isRemoteJob
                : !jobTypeNormalized ? true
                : t === 'contractor' ? jobTypeNormalized.includes('contract')
                : jobTypeNormalized.includes(t)
            );

            const salMin = job.jobMinSalary ?? 0;
            const salMax = job.jobMaxSalary ?? Infinity;
            const matchesSalary =
                job.jobMinSalary == null ||
                job.jobMaxSalary == null ||
                (salMin >= minSalary && salMax <= maxSalary);

            // Jobs without an experience label pass any experience selection —
            // same "unknown ≠ mismatch" rule as the score and type filters.
            const jobExpLevel = normalize(job.experienceLevel || '');
            const matchesExperience =
                selectedExperience.length === 0 ||
                !jobExpLevel ||
                selectedExperience.some(bucket =>
                    (EXPERIENCE_LEVEL_BUCKETS[bucket] || [bucket]).includes(jobExpLevel)
                );

            const matchesLocation =
                selectedCities.length === 0 ||
                selectedCities.some(city =>
                    job.jobLocation?.toLowerCase().includes(city.value.toLowerCase())
                );

            const matchesScore = (() => {
                if (selectedScoreRanges.length === 0) return true;
                // Filter by the SAME score the card displays: job.score, the ranked
                // match score on a 0-100 scale (the "23/100" badge). job.overallScore
                // is a different metric (company-culture average, 0-10) — filtering by
                // it while showing job.score let "23/100" jobs through "Excellent".
                // Jobs not yet ranked pass any range (unknown ≠ mismatch).
                const matchScore = typeof job.score === 'number' ? job.score : null;
                if (matchScore === null) return true;
                const inRange = {
                    excellent: matchScore >= 80,
                    good: matchScore >= 60 && matchScore < 80,
                    average: matchScore >= 40 && matchScore < 60,
                    poor: matchScore < 40,
                };
                return selectedScoreRanges.some(r => inRange[r]);
            })();

            return matchesSearch && matchesType && matchesSalary && matchesExperience && matchesLocation && matchesScore;
        });
    }, [enrichedJobsArray, debouncedQuery, selectedJobTypes, minSalary, maxSalary, selectedExperience, selectedCities, selectedScoreRanges]);

    // When each job was added to the user's list (UserJob.addedAt) — drives the
    // persistent "Fetched today" / "Last fetched" filters (survive page refresh).
    const jobAddedAtMs = (job) => {
        const t = job?.addedAt ? new Date(job.addedAt).getTime() : 0;
        return Number.isFinite(t) ? t : 0;
    };
    // A new fetch batch is considered to start after a gap of >60s between addedAt times.
    // The "last fetched" set is the most recent contiguous batch (jobs added together),
    // so two separate fetches don't get merged the way a fixed time-window did.
    const FETCH_GAP_MS = 60 * 1000;
    const { maxAddedAtMs, startOfTodayMs, lastBatchCutoffMs } = useMemo(() => {
        const times = (enrichedJobsArray || [])
            .map(jobAddedAtMs)
            .filter((t) => t > 0)
            .sort((a, b) => b - a); // newest first
        const maxA = times[0] || 0;
        // Walk down from the newest until we hit a gap bigger than FETCH_GAP_MS.
        let cutoff = maxA;
        for (let i = 1; i < times.length; i += 1) {
            if (times[i - 1] - times[i] > FETCH_GAP_MS) break;
            cutoff = times[i];
        }
        const sod = new Date();
        sod.setHours(0, 0, 0, 0);
        return { maxAddedAtMs: maxA, startOfTodayMs: sod.getTime(), lastBatchCutoffMs: cutoff };
    }, [enrichedJobsArray]);
    const isFetchedToday = (job) => jobAddedAtMs(job) >= startOfTodayMs;
    // Last fetched = membership in the most recent fetch's explicit ID set (exact, not time-based).
    const isLastFetched = (job) => lastFetchedIds.has(String(job?._id));
    // "NEW" badge: shown consistently for any job added in the last 24 hours (persists across refresh).
    const NEW_BADGE_WINDOW_MS = 24 * 60 * 60 * 1000;
    const isJobNew = (job) => jobAddedAtMs(job) >= Date.now() - NEW_BADGE_WINDOW_MS;

    const chipCounts = useMemo(() => {
        const isRecent = (job) => {
            const postedField = job.datentime || job.postedDate || job.postedAt;
            if (!postedField) return false;
            const date = postedField?.$date?.$numberLong
                ? new Date(parseInt(postedField.$date.$numberLong, 10))
                : new Date(postedField);
            return (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24) <= 7;
        };
        return {
            // True total from the backend (the loaded list is capped at 500); fall back to
            // the loaded length only if the total hasn't arrived yet.
            all: Math.max(totalJobs || 0, baseFilteredJobs.length),
            saved: baseFilteredJobs.filter(job =>
                savedJobs.some(s => (s.jobId?._id || s.jobId) === job._id)
            ).length,
            unsaved: baseFilteredJobs.filter(job =>
                !savedJobs.some(s => (s.jobId?._id || s.jobId) === job._id)
            ).length,
            excellent: baseFilteredJobs.filter(job => {
                const s = job.score ?? job.rankedScore;
                // Aligned with the backend's "Strong fit" threshold (70) — the old 85 bar
                // was unreachable, so this chip permanently showed 0.
                return (typeof s === 'number' && s >= 70) || job.matchLabel === 'Strong fit' || job.matchLabel === 'Strong match';
            }).length,
            referral: baseFilteredJobs.filter(job =>
                savedJobs.some(s => s.jobId?._id === job._id && s.organizationId)
            ).length,
            remote: baseFilteredJobs.filter(job =>
                job.jobLocation?.toLowerCase().includes('remote')
            ).length,
            recent: baseFilteredJobs.filter(isRecent).length,
            'fetched-today': baseFilteredJobs.filter(isFetchedToday).length,
            'last-fetched': baseFilteredJobs.filter(isLastFetched).length,
        };
    }, [baseFilteredJobs, savedJobs, maxAddedAtMs, startOfTodayMs, lastBatchCutoffMs, lastFetchedIds, totalJobs]);

    const displayJobs = useMemo(() => {
        const quickFiltered = baseFilteredJobs.filter((job) => {
            if (quickFilter === 'all') return true;
            if (quickFilter === 'fetched-today') {
                return isFetchedToday(job);
            }
            if (quickFilter === 'last-fetched') {
                return isLastFetched(job);
            }
            if (quickFilter === 'saved') {
                return savedJobs.some(s => (s.jobId?._id || s.jobId) === job._id);
            }
            if (quickFilter === 'unsaved') {
                return !savedJobs.some(s => (s.jobId?._id || s.jobId) === job._id);
            }
            if (quickFilter === 'excellent') {
                const s = job.score ?? job.rankedScore;
                return (typeof s === 'number' && s >= 70) || job.matchLabel === 'Strong fit' || job.matchLabel === 'Strong match';
            }
            if (quickFilter === 'referral') {
                const hasSavedContact = savedJobs.some(s => s.jobId?._id === job._id && s.organizationId);
                return hasSavedContact;
            }
            if (quickFilter === 'remote') {
                return job.jobLocation?.toLowerCase().includes('remote');
            }
            if (quickFilter === 'recent') {
                const postedField = job.datentime || job.postedDate || job.postedAt;
                if (!postedField) return false;
                let date;
                if (postedField?.$date?.$numberLong) {
                    date = new Date(parseInt(postedField.$date.$numberLong, 10));
                } else {
                    date = new Date(postedField);
                }
                const dayDelta = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24);
                return dayDelta <= 7;
            }
            return true;
        });

        return quickFiltered.sort((a, b) => {
            switch(sortBy) {
                case 'score': {
                    const aScore = a.score ?? a.rankedScore ?? (a.overallScore != null ? a.overallScore * 10 : null);
                    const bScore = b.score ?? b.rankedScore ?? (b.overallScore != null ? b.overallScore * 10 : null);
                    if (aScore == null && bScore == null) return 0;
                    if (aScore == null) return 1;
                    if (bScore == null) return -1;
                    return bScore - aScore;
                }

                case 'date':
                default:
                    const dateA = new Date(a.datentime || 0);
                    const dateB = new Date(b.datentime || 0);
                    return dateB - dateA;
            }
        });
    }, [baseFilteredJobs, quickFilter, sortBy, savedJobs, maxAddedAtMs, startOfTodayMs, lastBatchCutoffMs, lastFetchedIds]);

    // Calculate pagination
    const totalPages = Math.ceil(displayJobs.length / jobsPerPage);
    const paginatedDisplayJobs = useMemo(() => {
        const startIndex = (currentPage - 1) * jobsPerPage;
        const endIndex = startIndex + jobsPerPage;
        return displayJobs.slice(startIndex, endIndex);
    }, [displayJobs, currentPage, jobsPerPage]);

    useEffect(() => {
        const target = location.state?.chatTargetJob;
        if (!target || displayJobs.length === 0) return;

        const targetSignature = `${getStableId(target.id || target._id || target.jobId)}::${normalizeMatchText(target.title)}::${normalizeMatchText(target.company)}::${normalizeMatchText(target.location)}`;
        if (consumedChatTargetRef.current === targetSignature) return;

        const foundIndex = displayJobs.findIndex((job) => doesJobMatchChatTarget(job, target));
        if (foundIndex < 0) return;

        const targetPage = Math.floor(foundIndex / jobsPerPage) + 1;
        if (currentPage !== targetPage) {
            setCurrentPage(targetPage);
            return;
        }

        const matchedJob = displayJobs[foundIndex];
        const matchSignature = getJobMatchSignature(matchedJob);
        consumedChatTargetRef.current = targetSignature;

        requestAnimationFrame(() => {
            const selectorValue = encodeURIComponent(matchSignature);
            const container = centerJobsListRef.current;
            const el = container?.querySelector(`[data-job-scroll-key="${selectorValue}"]`);
            if (!el) return;

            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setChatScrollHighlightKey(matchSignature);
            setTimeout(() => setChatScrollHighlightKey((prev) => (prev === matchSignature ? '' : prev)), 2200);
            // Only scroll and highlight the matched job when navigated from chat.
            // Do NOT call `handleCardClick` here — that triggers the full card-open
            // workflow and can make the page appear to "break" when the user
            // only expects a lightweight scroll+highlight behavior.
        });
    }, [location.state, displayJobs, currentPage, jobsPerPage]);

    useEffect(() => {
        if (!scrollTargetJobId) return undefined;

        if (consumedScrollTargetJobIdRef.current === scrollTargetJobId) {
            return undefined;
        }

        const stableTargetId = getStableId(scrollTargetJobId);
        if (!stableTargetId) {
            dispatch(clearScrollTargetJobId());
            return undefined;
        }

        const foundIndex = displayJobs.findIndex((job) => {
            const jobId = getStableId(job?._id || job?.id || job?.jobId);
            return jobId && jobId === stableTargetId;
        });

        if (foundIndex < 0) {
            consumedScrollTargetJobIdRef.current = scrollTargetJobId;
            dispatch(clearScrollTargetJobId());
            return undefined;
        }

        const targetPage = Math.floor(foundIndex / jobsPerPage) + 1;
        if (currentPage !== targetPage) {
            setCurrentPage(targetPage);
            return undefined;
        }

        const scrollTimer = setTimeout(() => {
            const el = document.getElementById(stableTargetId);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.classList.add('highlight-job-card');

                setTimeout(() => {
                    el.classList.remove('highlight-job-card');
                }, 2000);
            }

            consumedScrollTargetJobIdRef.current = scrollTargetJobId;
            dispatch(clearScrollTargetJobId());
        }, 300);

        return () => clearTimeout(scrollTimer);
    }, [scrollTargetJobId, displayJobs, currentPage, jobsPerPage, dispatch]);

    const handleCardClick = async (job) => {
        console.log("🔍 [JobClick] Job clicked:", job);
        console.log("🔍 [JobClick] job.companyId:", job.companyId);
        console.log("Selected Jobs: ", selectedJobs);
        
        setJobProfileLoading(true);

        try {
            const companyFromStore = companies.find(
                (company) => company._id === job.companyId?._id || company._id === job.companyId
            );

            // Use job.companyId if it exists (populated), otherwise use companyFromStore
            const fullCompanyData = (typeof job.companyId === 'object' ? job.companyId : companyFromStore);
            
            console.log("🔍 [JobClick] fullCompanyData:", fullCompanyData);

            const companyData = fullCompanyData || {
                _id: job.companyId || job._id,
                name: job.companyName || 'Unknown',
                companyLogo: job.companyLogo || 'https://static.thenounproject.com/png/4149271-200.png',
                hqLocation: job.jobLocation || 'Unknown',
                email: job.email || 'N/A',
                description: job.jobDesc || 'No description available',
            };

            // Set the full company data including scores
            const selectedCompanyData = {
                _id: companyData._id,
                name: companyData.name || job.companyName,
                companyLogo: companyData.companyLogo || job.companyLogo || 'https://static.thenounproject.com/png/4149271-200.png',
                hqLocation: companyData.hqLocation || job.jobLocation,
                email: companyData.email || job.email || 'N/A',
                description: companyData.description || job.jobDesc || 'No description available',
                scores: companyData.scores,
                fitScore: companyData.fitScore,
                redFlags: companyData.redFlags,
                limitations: companyData.limitations,
                scoresStatus: companyData.scoresStatus,
            };
            
            console.log("🎯 [JobClick] Setting selectedCompany:", selectedCompanyData);
            setSelectedCompany(selectedCompanyData);
            setActiveRightTab('insights'); // Switch to Company Insights tab

            const matchingSavedJob = savedJobs.find(saved => saved.jobId._id === job._id);
            const organizationId = matchingSavedJob?.organizationId;

            if (organizationId) {
                await dispatch(fetchAllContactsByCompanyId(organizationId));
            } else {
                console.warn("⚠️ Organization ID not found for this job.");
            }

        } catch (err) {
            console.error('Error in handleCardClick:', err.message);
        } finally {
            setJobProfileLoading(false);
        }
    };

    const handleSaveSelectedJobs = async () => {
        setLocalLoading(true);
        setSavingJobs(true);
        setSaveJobsProgress("Saving jobs...");
        
        try {
            const savedJobIds = savedJobs.map((job) => job.jobId);
            const newJobsToSave = selectedJobs.filter(
                (job) => !savedJobIds.includes(job._id)
            );

            if (newJobsToSave.length === 0) {
                console.log("No new jobs to save.")
                setToastMessage({ show: true, message: 'All selected jobs are already saved. You are up to date!', type: 'info' });
                setSavingJobs(false);
                return;
            }

            // Prepare recruiter data for jobs that have fetched recruiters
            const recruiterDataToSend = {};
            newJobsToSave.forEach((job) => {
                if (recruitersByJobId[job._id]) {
                    recruiterDataToSend[job._id] = recruitersByJobId[job._id];
                }
            });

            console.log('💼 Saving jobs with recruiter data:', Object.keys(recruiterDataToSend).length, 'jobs have recruiters');

            // Stage 1: Save jobs
            setSaveJobsProgress(`Saving ${newJobsToSave.length} jobs...`);
            const saved = await dispatch(
                saveMultipleJobs({
                    userId: user._id,
                    jobs: newJobsToSave.map((job) => ({
                        jobId: job._id,
                        companyId: job.companyId,
                        companyName: job.companyName,
                    })),
                    recruiterData: Object.keys(recruiterDataToSend).length > 0 ? recruiterDataToSend : undefined,
                })
            ).unwrap();

            const { insertedJobs, failedToLinkCount } = saved;
            const successfullyLinkedCount = insertedJobs.length - failedToLinkCount;

            let toastMsg = `${successfullyLinkedCount} of ${insertedJobs.length} jobs were linked successfully.`;
            let toastType = 'success';

            if (successfullyLinkedCount === 0 && insertedJobs.length > 0) {
                setToastMessage({
                    show: true,
                    message: `Couldn't find contacts for any of the ${insertedJobs.length} new jobs.`,
                    type: 'error'
                })
                return
            }

            // 🎯 KEY OPTIMIZATION: Save recruiter contacts immediately after jobs are saved
            const recruiterSavePromises = [];
            let recruitersSavedCount = 0;
            let recruitersAutoFetched = 0;
            
            // Stage 2: Fetch recruiters
            setSaveJobsProgress("Fetching recruiters for saved jobs...");
            
            for (const insertedJob of insertedJobs) {
                const jobId = insertedJob.jobId._id || insertedJob.jobId;
                let recruiterData = recruitersByJobId[jobId];
                
                // 🚀 AUTO-FETCH: If no recruiter data in Redux, fetch it now!
                if (!recruiterData && insertedJob.organizationId) {
                    console.log(`🔄 Auto-fetching recruiters for job ${jobId} (not clicked "Show Recruiter" yet)`);
                    setSaveJobsProgress(`Finding recruiters for ${newJobsToSave.find(j => j._id === jobId)?.companyName || 'company'}...`);
                    try {
                        const job = newJobsToSave.find(j => j._id === jobId);
                        if (job) {
                            recruiterData = await dispatch(fetchRecruiterForJob({ jobDetails: job })).unwrap();
                            if (recruiterData) {
                                recruitersAutoFetched++;
                                console.log(`✅ Auto-fetched recruiters for ${job.companyName}`);
                            }
                        }
                    } catch (err) {
                        console.warn(`⚠️ Failed to auto-fetch recruiters for job ${jobId}:`, err.message);
                        // Continue without recruiters for this job
                    }
                }
                
                // Now save recruiters if we have them (either from Redux or auto-fetched)
                if (recruiterData) {
                    console.log(`💾 Saving recruiters for job ${jobId} to org ${insertedJob.organizationId}`);
                    
                    recruiterSavePromises.push(
                        dispatch(saveRecruiterContacts({
                            jobId,
                            companyId: insertedJob.companyId,
                            organizationId: insertedJob.organizationId,
                            recruiterData
                        })).unwrap().then(() => {
                            recruitersSavedCount++;
                            console.log(`✅ Successfully saved recruiters for job ${jobId}`);
                        }).catch(err => {
                            console.error(`❌ Failed to save recruiters for job ${jobId}:`, err);
                        })
                    );
                }
            }

            // Stage 3: Save recruiters to database
            if (recruiterSavePromises.length > 0) {
                setSaveJobsProgress(`Saving ${recruiterSavePromises.length} recruiter contacts...`);
                console.log(`⏳ Waiting for ${recruiterSavePromises.length} recruiter save operations...`);
                await Promise.allSettled(recruiterSavePromises);
                console.log(`✅ Saved recruiters for ${recruitersSavedCount}/${recruiterSavePromises.length} jobs`);
                
                if (recruitersAutoFetched > 0) {
                    console.log(`🚀 Auto-fetched recruiters for ${recruitersAutoFetched} jobs`);
                }
                
                if (recruitersSavedCount > 0) {
                    toastMsg = `${successfullyLinkedCount} jobs saved! ${recruitersSavedCount} jobs have recruiters ready to connect.`;
                }
            }

            const newOrgIds = insertedJobs
                .map(job => job.organizationId)
                .filter(Boolean);

            // 🚀 OPTIMIZATION: Only start contact search for jobs that DON'T have recruiter data
            const orgIdsNeedingSearch = insertedJobs
                .filter(job => {
                    const jobId = job.jobId._id || job.jobId;
                    return !recruitersByJobId[jobId] && job.organizationId;
                })
                .map(job => job.organizationId)
                .filter(Boolean);

            // Stage 4: Start contact search for remaining jobs
            if (orgIdsNeedingSearch.length > 0) {
                setSaveJobsProgress(`Initiating contact search for ${orgIdsNeedingSearch.length} companies...`);
                console.log(`🔍 Starting contact search for ${orgIdsNeedingSearch.length} jobs without recruiter data`);
                const payload = {
                    preferredIndustry: user.preferredIndustry || [],
                    preferredDepartment: user.preferredDepartment || [],
                    organizationId: orgIdsNeedingSearch,
                };
                await api.post("/api/othercontacts/startcontactsearch", payload);
                dispatch(startRealtimeSearch());
            } else {
                console.log(`✅ All jobs have recruiters - skipping contact search!`);
            }

            // Stage 5: Finalizing
            setSaveJobsProgress("Finalizing...");
            console.log("✅ Job saving and contact linking complete!");

            // Refresh saved jobs to show the "Saved" tag immediately
            await dispatch(fetchSavedJobs(user._id));

            // Show success message and stay on jobs page
            setToastMessage({ 
                show: true, 
                message: toastMsg, 
                type: toastType 
            });
            
            // Clear selected jobs
            setSelectedJobs([]);
            
        } catch (error) {
            console.error(error);
            setToastMessage({ show: true, message: "An error occurred during saving or fetching contacts.", type: 'error' });
        } finally {
            setLocalLoading(false);
            setSavingJobs(false);
        }
    };

    const handleOpenSidebar = () => {
        setJobsidebarOpen(!jobsidebarOpen);
    }

    const clearFilters = () => {
        setQuery("");
        setSelectedJobTypes([]);
        setMinSalary(0);
        setMaxSalary(1000000);
        setSelectedExperience([]);
        setSelectedCities([]);
        setLocationQuery("");
        setSelectedScoreRanges([]);
        
        setToastMessage({
            show: true,
            message: 'All filters have been cleared',
            type: 'success'
        });
        
        setTimeout(() => {
            setToastMessage({ ...toastMessage, show: false });
        }, 3000);
    }
    
    const handleFetchMoreJobs = () => {
        // Show modal instead of directly fetching
        setShowFetchJobsModal(true);
    };

    const handleFetchByPreference = async (jobCount = 50, postedWithinHours = null) => {
        setShowFetchJobsModal(false);
        setLocalLoading(true);

        try {
            const result = await dispatch(fetchNewJobs({ limit: jobCount, postedWithinHours })).unwrap();

            // Backend now returns: { jobs: [], newCount: 50, totalFetched: 350, message: "..." }
            const newJobsCount = result?.newCount || 0;

            // Surface the just-fetched jobs via the "Just fetched" filter (result.jobs are the new ones).
            markRecentlyFetched((Array.isArray(result?.jobs) ? result.jobs : []).map((j) => j._id));

            if (result?.liveSearchUnavailable) {
                // JSearch quota exhausted — cached jobs were still shown.
                setToastMessage({
                    show: true,
                    message: 'Live job search is temporarily unavailable — showing saved jobs for now. Please try again later.',
                    type: 'info'
                });
            } else if (newJobsCount > 0) {
                // Scroll to top to see new jobs
                window.scrollTo({ top: 0, behavior: 'smooth' });
                
                // Show job notification
                setJobsLoadedCount(newJobsCount);
                setShowJobNotification(true);
                
                setToastMessage({
                    show: true,
                    message: `✅ ${newJobsCount} new jobs added based on your preferences! · ${newJobsCount} credit${newJobsCount === 1 ? '' : 's'} used`,
                    type: 'success'
                });
            } else {
                setToastMessage({
                    show: true,
                    message: 'No new jobs right now — try again later, or add more target roles/locations in your profile for more results.',
                    type: 'info'
                });
            }
        } catch (error) {
            console.error('Error fetching jobs by preference:', error);
            setToastMessage({
                show: true,
                message: error.message || 'Failed to fetch jobs. Please try again.',
                type: 'error'
            });
        } finally {
            setLocalLoading(false);
            setTimeout(() => {
                setToastMessage(prev => ({ ...prev, show: false }));
            }, 5000);
        }
    };

    const handleFetchATSOnly = async (jobCount = 50, postedWithinHours = null) => {
        setShowFetchJobsModal(false);
        setLocalLoading(true);
        setAtsFetchLoading(true);

        try {
            // Snapshot existing job IDs so we can identify exactly what this fetch adds.
            const beforeIds = new Set(enrichedJobsArray.map((j) => String(j._id)));

            const response = await api.post('/api/jobs/fetch-ats-jobs-only', { limit: jobCount, ...(postedWithinHours ? { postedWithinHours } : {}) });
            // Count only what was ACTUALLY added to the user's list. newCount is the number
            // MATCHED (may already be in the list), so it must not inflate the "added" count.
            const backendAdded = (typeof response.data?.addedToUser === 'number') ? response.data.addedToUser : null;

            // Additive: reload the user's FULL job list so the new ATS jobs appear ALONGSIDE
            // existing jobs (replacing the list with only the ATS subset was resetting the
            // "All" count and wiping "Fetched today" — the replaced jobs had no addedAt).
            const fullList = await dispatch(fetchUserJobs()).unwrap();
            const newIds = (Array.isArray(fullList) ? fullList : [])
                .filter((j) => !beforeIds.has(String(j._id)))
                .map((j) => j._id);
            // "Last fetched" = the jobs THIS fetch returned (from the response). The net-new
            // diff undercounts once the user is over the 500-row list cap, so prefer the
            // response's own ids and fall back to the diff only if absent.
            const fetchedIds = (Array.isArray(response.data?.jobs) ? response.data.jobs : [])
                .map((j) => j._id).filter(Boolean);
            const lastFetchedMark = fetchedIds.length > 0 ? fetchedIds : newIds;
            if (lastFetchedMark.length > 0) markRecentlyFetched(lastFetchedMark);

            const addedToUser = backendAdded !== null ? backendAdded : newIds.length;
            if (addedToUser > 0) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setJobsLoadedCount(addedToUser);
                setShowJobNotification(true);
                setToastMessage({
                    show: true,
                    message: `✅ ${addedToUser} ATS jobs added to your list! · Free — no credits used`,
                    type: 'success'
                });
            } else {
                setToastMessage({
                    show: true,
                    message: response.data?.message || 'No new ATS jobs found right now.',
                    type: 'info'
                });
            }
        } catch (error) {
            console.error('Error fetching ATS jobs:', error);
            setToastMessage({
                show: true,
                message: error.response?.data?.message || error.message || 'Failed to fetch ATS jobs. Please try again.',
                type: 'error'
            });
        } finally {
            setLocalLoading(false);
            setAtsFetchLoading(false);
            setTimeout(() => {
                setToastMessage(prev => ({ ...prev, show: false }));
            }, 5000);
        }
    };

    const handleFetchATSByKeyword = async (keywords, jobCount = 50, postedWithinHours = null) => {
        setShowFetchJobsModal(false);
        setLocalLoading(true);
        setAtsFetchLoading(true);

        try {
            // Snapshot existing job IDs so we can identify exactly what this fetch adds.
            const beforeIds = new Set(enrichedJobsArray.map((j) => String(j._id)));

            const response = await api.post('/api/jobs/fetch-ats-jobs-only', {
                limit: jobCount,
                keywords,
                ...(postedWithinHours ? { postedWithinHours } : {})
            });
            // Count only what was ACTUALLY added to the user's list. newCount is the number
            // MATCHED (may already be in the list), so it must not inflate the "added" count.
            const backendAdded = (typeof response.data?.addedToUser === 'number') ? response.data.addedToUser : null;

            // Additive: reload the user's FULL job list so the new ATS jobs appear ALONGSIDE
            // existing jobs.
            const fullList = await dispatch(fetchUserJobs()).unwrap();
            const newIds = (Array.isArray(fullList) ? fullList : [])
                .filter((j) => !beforeIds.has(String(j._id)))
                .map((j) => j._id);
            // "Last fetched" = the jobs THIS fetch returned (from the response). The net-new
            // diff undercounts once the user is over the 500-row list cap, so prefer the
            // response's own ids and fall back to the diff only if absent.
            const fetchedIds = (Array.isArray(response.data?.jobs) ? response.data.jobs : [])
                .map((j) => j._id).filter(Boolean);
            const lastFetchedMark = fetchedIds.length > 0 ? fetchedIds : newIds;
            if (lastFetchedMark.length > 0) markRecentlyFetched(lastFetchedMark);

            const addedToUser = backendAdded !== null ? backendAdded : newIds.length;
            if (addedToUser > 0) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setJobsLoadedCount(addedToUser);
                setShowJobNotification(true);
                setToastMessage({
                    show: true,
                    message: `✅ ${addedToUser} ATS jobs matching your keywords added! · Free — no credits used`,
                    type: 'success'
                });
            } else {
                setToastMessage({
                    show: true,
                    message: response.data?.message || 'No ATS jobs matched your keywords. Try broader keywords or a wider time window.',
                    type: 'info'
                });
            }
        } catch (error) {
            console.error('Error fetching ATS jobs by keyword:', error);
            setToastMessage({
                show: true,
                message: error.response?.data?.message || error.message || 'Failed to fetch ATS jobs. Please try again.',
                type: 'error'
            });
        } finally {
            setLocalLoading(false);
            setAtsFetchLoading(false);
            setTimeout(() => {
                setToastMessage(prev => ({ ...prev, show: false }));
            }, 5000);
        }
    };

    const handleFetchByKeyword = async (keywords, jobCount = 50, postedWithinHours = null) => {
        setShowFetchJobsModal(false);
        setKeywordFetchLoading(true);

        try {
            const token = localStorage.getItem("token");

            console.log('🔍 Fetching jobs by keyword:', keywords);

            // Snapshot existing job IDs so we can identify exactly what this fetch adds.
            const beforeIds = new Set(enrichedJobsArray.map((j) => String(j._id)));
            console.log(`📊 Current jobs count: ${beforeIds.size}`);

            const response = await api.post('/api/jobs/fetch-jobs-by-keyword',
                { keywords, limit: jobCount, ...(postedWithinHours ? { postedWithinHours } : {}) },
                {
                    headers: {
                        "auth-token": token
                    }
                }
            );

            console.log('📊 Full backend response:', response.data);

            // Check backend response for newJobsCount first
            const backendNewJobs = response.data?.newJobsCount || response.data?.count || 0;
            console.log(`📊 Backend says new jobs: ${backendNewJobs}`);

            // Additive: reload the user's full job list so the newly-added keyword jobs
            // appear ALONGSIDE existing jobs, instead of replacing the view with only the
            // keyword matches (which made the list shrink to just the matched subset).
            const fullList = await dispatch(fetchUserJobs()).unwrap();

            // Identify the just-added jobs (in the new list, not in the old) and surface
            // them via the "Just fetched" filter so they don't get lost in the full list.
            const newIds = (Array.isArray(fullList) ? fullList : [])
                .filter((j) => !beforeIds.has(String(j._id)))
                .map((j) => j._id);
            markRecentlyFetched(newIds);

            // Trust the backend's count, falling back to the diff we computed.
            const finalNewJobsCount = backendNewJobs || newIds.length;
            console.log(`✅ Final new jobs count: ${finalNewJobsCount}`);
            
            if (finalNewJobsCount > 0) {
                // Scroll to top to see new jobs
                setTimeout(() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }, 100);
                
                // Show success notification with correct count
                setJobsLoadedCount(finalNewJobsCount);
                setShowJobNotification(true);
                
                setToastMessage({
                    show: true,
                    message: `✅ ${finalNewJobsCount} new jobs added to your list! · ${finalNewJobsCount} credit${finalNewJobsCount === 1 ? '' : 's'} used`,
                    type: 'success'
                });
            } else {
                // Check if backend found jobs but they were already in user's list
                const backendMessage = response.data?.message;
                const backendCount = response.data?.count || response.data?.newJobsCount;
                
                if (backendCount > 0) {
                    // Backend found jobs but user already has them
                    setToastMessage({
                        show: true,
                        message: backendMessage || `Found ${backendCount} jobs, but they're already in your list. Try different keywords!`,
                        type: 'info'
                    });
                } else {
                    setToastMessage({
                        show: true,
                        message: backendMessage || 'No jobs found matching those keywords. Try different search terms.',
                        type: 'info'
                    });
                }
            }
        } catch (error) {
            console.error('❌ Error fetching jobs by keyword:', error);
            console.error('❌ Error details:', error.response?.data);
            
            setToastMessage({
                show: true,
                message: error.response?.data?.message || 'Failed to fetch jobs. Please try again.',
                type: 'error'
            });
        } finally {
            setKeywordFetchLoading(false);
            setTimeout(() => {
                setToastMessage(prev => ({ ...prev, show: false }));
            }, 5000);
        }
    };

    // 🆕 Background batch analysis function (runs without opening modal)
    const runBackgroundAnalysis = async (jobEntity, recruiterDataObject, stableJobId = null) => {
        if (!jobEntity || !recruiterDataObject) {
            console.warn('[BackgroundAnalysis] ⚠️ Missing job data or recruiterData');
            return;
        }

        const jobId = stableJobId || jobEntity?._id || jobEntity?.jobId || jobEntity?.id || getCompanyKey(jobEntity);
        
        // Extract actual contacts from the data structure
        const allContacts = [];
        
        if (recruiterDataObject.hiring_manager?.best_guesses) {
            allContacts.push(...recruiterDataObject.hiring_manager.best_guesses);
        }
        if (recruiterDataObject.recruiters?.best_guesses) {
            allContacts.push(...recruiterDataObject.recruiters.best_guesses);
        }

        if (allContacts.length === 0) {
            console.warn('[BackgroundAnalysis] ⚠️ No contacts found in data structure for job:', jobId);
            console.log('[BackgroundAnalysis] 📊 recruiterData structure:', JSON.stringify(recruiterDataObject, null, 2));
            
            // Mark as complete even without analysis since there's nothing to analyze
            handleUpdateCompanyAnalysis(jobId, { 
                message: 'No contacts available for analysis',
                contacts: []
            }, { analysisComplete: true, strategyComplete: true });
            return;
        }

        console.log('🚀 [BackgroundAnalysis] Starting for job', jobId, 'with', allContacts.length, 'contacts');
        console.log('   📋 Contact names:', allContacts.map(c => c.name).join(', '));
        
        // Find the job object from enrichedJobsArray to get full details (fallback to provided entity)
        const job = jobEntity || enrichedJobsArray.find(j => j._id === jobId) || {};

        try {
            console.log('🌐 [BackgroundAnalysis] Making API call to /api/recruitercontacts/batch-analyze-contacts');
            
            const jobDetails = {
                companyName: job.companyName,
                jobPosition: job.jobPosition,
                jobLocation: job.jobLocation,
                jobDesc: job.jobDesc,
                job_department_if_any: job.department || null
            };

            const userProfile = user ? {
                name: user.name,
                jobTitle: user.jobTitle || user.currentTitle,
                currentTitle: user.currentTitle,
                experience: user.experience,
                skills: user.skills || [],
                education: user.education || [],
                experience_details: user.experience || [],
                resume: user.resume || '',
                preferredDepartment: user.preferredDepartment || [],
                preferredIndustry: user.preferredIndustry || [],
                previousIndustryYears: user.previousIndustryYears || []
            } : {};

            const response = await api.post('/api/recruitercontacts/batch-analyze-contacts', {
                allContacts: allContacts.map(contact => ({
                    name: contact.name,
                    title: contact.title,
                    department: contact.department || contact._fullData?.department,
                    location: contact.location,
                    overall_experience_years: contact._fullData?.overall_experience_years,
                    company_experience_years: contact._fullData?.company_experience_years,
                    email: contact.email,
                    profile_url: contact.profile_url,
                    confidence_score: contact.confidence_score,
                    referral_probability: contact.referral_probability
                })),
                jobDetails,
                userProfile
            });

            console.log('📦 [BackgroundAnalysis] API Response received:', response.status);
            console.log('📊 [BackgroundAnalysis] Response data keys:', Object.keys(response.data || {}));

            const analysisMap = {};
            // Local references to sections of recruiter data
            const hm = recruiterDataObject.hiring_manager;
            const rc = recruiterDataObject.recruiters;

            (hm?.best_guesses || []).forEach((person, idx) => {
                const contactResponse = response.data.contacts?.find(c => c.name === person.name);
                if (contactResponse?.aiAnalysis) {
                    analysisMap[`hiring_manager-${idx}`] = {
                        success: true,
                        reasoning: contactResponse.aiAnalysis,
                        source: response.data.source || 'deepseek'
                    };
                }
            });
            (rc?.best_guesses || []).forEach((person, idx) => {
                const contactResponse = response.data.contacts?.find(c => c.name === person.name);
                if (contactResponse?.aiAnalysis) {
                    analysisMap[`recruiters-${idx}`] = {
                        success: true,
                        reasoning: contactResponse.aiAnalysis,
                        source: response.data.source || 'deepseek'
                    };
                }
            });

            console.log('✅ [BackgroundAnalysis] Analysis complete for job:', jobId);
            console.log('   ✓ Analyzed contacts:', Object.keys(analysisMap).length);
            console.log('   ✓ Analysis keys:', Object.keys(analysisMap));
            console.log('   📞 Calling handleUpdateCompanyAnalysis with jobId:', jobId);
            
            // Always call to unblock the card, even if analysisMap is empty
            handleUpdateCompanyAnalysis(jobId, Object.keys(analysisMap).length > 0 ? analysisMap : {
                message: 'Analysis completed but no AI insights generated',
                contacts: allContacts.map(c => ({ name: c.name, title: c.title }))
            }, { analysisComplete: true });

            const organizationId = deriveOrganizationId(job, recruiterDataObject);
            await requestStrategyForOrg({ orgId: organizationId, jobData: job, contacts: allContacts });
            
        } catch (error) {
            console.error('❌ [BackgroundAnalysis] Failed for job', jobId);
            console.error('   ❌ Error message:', error.message);
            console.error('   ❌ Error status:', error.response?.status);
            console.error('   ❌ Error data:', error.response?.data);
            console.error('   📞 Calling handleUpdateCompanyAnalysis with fallback data');
            
            // Mark as complete with error to unblock UI - card will still show View button
            handleUpdateCompanyAnalysis(jobId, {
                error: true,
                message: error.response?.data?.message || error.message,
                contacts: allContacts.map(c => ({
                    name: c.name,
                    title: c.title,
                    fallback: true
                }))
            }, { analysisComplete: true });

            const organizationId = deriveOrganizationId(job, recruiterDataObject);
            await requestStrategyForOrg({ orgId: organizationId, jobData: job, contacts: allContacts });
        }
    };

    // 🆕 Unified handler to add or update company card (prevents duplicates)
    const handleAddOrUpdateCompanyCard = (job, recruiterData = null) => {
        console.log('🚀 [Jobs] ==================== handleAddOrUpdateCompanyCard START ====================');
        console.log('   Job:', job?.companyName, 'ID:', job?._id);
        console.log('   Has recruiterData:', !!recruiterData);
        if (recruiterData) {
            console.log('   RecruiterData keys:', Object.keys(recruiterData));
            console.log('   Hiring managers:', recruiterData.hiring_manager?.best_guesses?.length || 0);
            console.log('   Recruiters:', recruiterData.recruiters?.best_guesses?.length || 0);
        }
        
        if (!job) {
            console.warn('⚠️ [Jobs] No job provided to handleAddOrUpdateCompanyCard');
            return;
        }

        const companyKey = getCompanyKey(job);
        const stableJobId = job._id || job.jobId || job.id || companyKey;
        console.log('📇 [Jobs] Company key:', companyKey);
        const organizationId = deriveOrganizationId(job, recruiterData);
        console.log('   Derived organizationId:', organizationId);

        // Check if already processing
        const isProcessing = processingCompaniesRef.current.has(companyKey);
        console.log('   Is already in processing set:', isProcessing);
        console.log('   Has recruiterData:', !!recruiterData);
        
        // Only skip if we're already processing AND this is a duplicate loading call (no data)
        // Allow updates when we have actual recruiter data
        if (isProcessing && !recruiterData) {
            console.log('⏭️ [Jobs] Already processing', companyKey, 'and no new data - skipping duplicate loading call');
            return;
        }
        
        if (isProcessing && recruiterData) {
            console.log('🔄 [Jobs] Already processing', companyKey, 'but we have recruiter data - proceeding with update');
        }

        setFetchedCompanies(prev => {
            console.log('   Current cards count:', prev.length);
            const idx = prev.findIndex(c => c.companyKey === companyKey);
            console.log('   Found existing card at index:', idx);
            
            if (idx === -1) {
                // New card - ADD AT TOP
                const newCard = {
                    companyKey,
                    jobId: stableJobId,
                    jobData: job,
                    organizationId,
                    recruiterData: recruiterData || null,
                    analyzedData: null,
                    fetchFailed: Boolean(recruiterData?.fetchFailed),
                    errorMessage: recruiterData?.errorMessage || null,
                    analysisComplete: false,
                    strategyComplete: organizationId ? !!(referralStrategies[organizationId]?.data) : false,
                    status: recruiterData ? 'analyzing' : 'loading',
                    createdAt: Date.now()
                };
                newCard.status = computeCardStatus(newCard);
                
                console.log('➕ [Jobs] Adding new card at TOP:', companyKey, 'status:', newCard.status);
                processingCompaniesRef.current.add(companyKey);

                // Check if we actually have contacts before triggering analysis
                if (recruiterData) {
                    const hasContacts = 
                        (recruiterData.hiring_manager?.best_guesses?.length > 0) ||
                        (recruiterData.recruiters?.best_guesses?.length > 0);
                    
                    console.log('   Checking contacts... hasContacts:', hasContacts);
                    
                    if (hasContacts) {
                        const totalContacts = (recruiterData.hiring_manager?.best_guesses?.length || 0) + 
                                            (recruiterData.recruiters?.best_guesses?.length || 0);
                        console.log('   🎯 Triggering analysis for new card with', totalContacts, 'contacts');
                        console.log('   ⏰ Scheduling runBackgroundAnalysis in 100ms...');
                        setTimeout(() => {
                            console.log('⏰ [Jobs] Timeout fired - calling runBackgroundAnalysis now!');
                            runBackgroundAnalysis(job, recruiterData, stableJobId);
                        }, 100);
                    } else {
                        console.warn('   ⚠️ No contacts found in recruiterData for:', companyKey);
                        console.warn('   RecruiterData structure:', JSON.stringify(recruiterData, null, 2));
                    }
                }

                // Add at beginning of array (TOP)
                console.log('   ✅ Returning updated array with new card at top');
                return [newCard, ...prev];
            } else {
                // Update existing card
                const existing = prev[idx];
                
                console.log('🔄 [Jobs] Found existing card at index:', idx);
                console.log('   Existing status:', existing.status);
                console.log('   Existing has data:', !!existing.recruiterData);
                console.log('   Existing has analysis:', !!existing.analyzedData);
                
                // Check if we have actual contacts in new data
                const hasNewContacts = recruiterData && (
                    (recruiterData.hiring_manager?.best_guesses?.length > 0) ||
                    (recruiterData.recruiters?.best_guesses?.length > 0)
                );

                const updated = {
                    ...existing,
                    recruiterData: recruiterData || existing.recruiterData,
                    organizationId: existing.organizationId || organizationId,
                    fetchFailed: Boolean(recruiterData?.fetchFailed) || existing.fetchFailed,
                    errorMessage: recruiterData?.errorMessage || existing.errorMessage || null
                };
                if (typeof updated.analysisComplete !== 'boolean') {
                    updated.analysisComplete = !!existing.analyzedData;
                }
                if (typeof updated.strategyComplete !== 'boolean' && updated.organizationId) {
                    updated.strategyComplete = !!(referralStrategies[updated.organizationId]?.data);
                }
                updated.status = computeCardStatus(updated);

                console.log('🔄 [Jobs] Updating existing card:', companyKey);
                console.log('   Status change:', existing.status, '→', updated.status);
                console.log('   existing.analyzedData:', !!existing.analyzedData);
                console.log('   hasNewContacts:', hasNewContacts);
                console.log('   recruiterData provided:', !!recruiterData);

                // Trigger analysis if we just got new contacts and haven't analyzed yet
                if (!existing.analyzedData && hasNewContacts) {
                    console.log('   ✅ Conditions met for analysis trigger');
                    
                    const existingHasContacts = existing.recruiterData && (
                        (existing.recruiterData.hiring_manager?.best_guesses?.length > 0) ||
                        (existing.recruiterData.recruiters?.best_guesses?.length > 0)
                    );

                    console.log('   existingHasContacts:', existingHasContacts);

                    if (!existingHasContacts) {
                        console.log('   🎯 Triggering analysis for updated card');
                        console.log('   ⏰ Scheduling runBackgroundAnalysis in 100ms...');
                        setTimeout(() => {
                            console.log('⏰ [Jobs] UPDATE Timeout fired - calling runBackgroundAnalysis now!');
                            runBackgroundAnalysis(job, recruiterData, stableJobId);
                        }, 100);
                    } else {
                        console.log('   ⏭️ Existing card already has contacts - not triggering analysis again');
                    }
                } else {
                    console.log('   ⏭️ Not triggering analysis:');
                    console.log('      - existing.analyzedData:', !!existing.analyzedData);
                    console.log('      - hasNewContacts:', hasNewContacts);
                }

                // Move updated card to TOP
                const clone = [...prev];
                clone.splice(idx, 1); // Remove from old position
                clone.unshift(updated); // Add to beginning
                
                console.log('   ✅ Returning updated array with card moved to top');
                return clone;
            }
        });
        
        setActiveRightTab('recruiters');
    };

    // 🆕 Handler to update company card with analyzed data
    const handleUpdateCompanyAnalysis = (jobId, analyzedData, updates = {}) => {
        if (!jobId) {
            console.warn('⚠️ [Jobs] handleUpdateCompanyAnalysis called without jobId');
            return;
        }

        console.log('📊 [Jobs] handleUpdateCompanyAnalysis called:');
        console.log('   jobId:', jobId);
        console.log('   analyzedData keys:', analyzedData ? Object.keys(analyzedData).length : 0);

        setFetchedCompanies(prev => {
            console.log('   Current cards count:', prev.length);
            console.log('   Card jobIds:', prev.map(c => c.jobId));
            
            const idx = prev.findIndex(c => c.jobId === jobId);
            if (idx === -1) {
                console.warn('⚠️ [Jobs] No card found for jobId:', jobId);
                console.warn('   Available jobIds:', prev.map(c => ({ key: c.companyKey, jobId: c.jobId })));
                return prev;
            }

            const updated = {
                ...prev[idx],
                analyzedData: analyzedData || prev[idx].analyzedData || {}
            };
            if (typeof updates.analysisComplete === 'boolean') {
                updated.analysisComplete = updates.analysisComplete;
            } else if (analyzedData) {
                updated.analysisComplete = true;
            }
            if (typeof updates.strategyComplete === 'boolean') {
                updated.strategyComplete = updates.strategyComplete;
            }
            if (!updated.organizationId) {
                updated.organizationId = deriveOrganizationId(updated.jobData, updated.recruiterData);
            }
            updated.status = computeCardStatus(updated);

            console.log('✅ [Jobs] Card found at index', idx, '- updating state:', prev[idx].companyKey);
            console.log('   Old status:', prev[idx].status, '→ New status:', updated.status);

            if (updated.status === 'complete') {
                processingCompaniesRef.current.delete(prev[idx].companyKey);
            }

            const clone = [...prev];
            clone[idx] = updated;
            return clone;
        });
    };

    // 🆕 Handler to view a company's recruiters
    const handleViewCompany = (company) => {
        console.log("👁️ [Jobs] Viewing company:", company.name);
        
        setActiveCompanyModal(company);
        setIsRecruiterModalOpen(true);
    };

    // 🆕 Handler to close recruiter modal
    const handleCloseRecruiterModal = () => {
        setIsRecruiterModalOpen(false);
        setActiveCompanyModal(null);
    };

    const handleDeleteCompanyCard = async (company) => {
        setFetchedCompanies((prev) => prev.filter((card) => card.companyKey !== company.companyKey));
        processingCompaniesRef.current.delete(company.companyKey);

        const jobId = getStableId(company?.jobId || company?.jobData?._id || company?.jobData?.jobId);
        if (!jobId) return;

        try {
            await api.delete(`/api/recruitercontacts/job-referral-analyses/${jobId}`);
        } catch (error) {
            console.warn('[Jobs] Failed to delete persisted referral analysis:', error?.message || error);
        }
    };

    const gamifiedMessages = [
        "Connecting to job sources...",
        "Scanning for new opportunities...",
        "Filtering relevant positions...",
        "Almost there..."
    ];

    const saveJobsMessages = [
        "Saving jobs...",
        "Fetching recruiters...",
        "Preparing your network...",
        "Almost done..."
    ];

    const isFetchingJobs =
        (isFirstJobsVisit && (((effectiveUserId && !initialJobsFetched) || jobsLoading || syncing) && displayJobs.length === 0)) ||
        loadingMoreJobs ||
        keywordFetchLoading ||
        atsFetchLoading;

    // Every fetch starts expanded: minimising one run must not silently hide the
    // next. Keyed on the rising edge so it does not fight the user mid-fetch.
    // The same edges own the reload marker, so all four fetch triggers are covered
    // without touching each handler.
    const wasFetchingJobs = useRef(false);
    useEffect(() => {
        if (isFetchingJobs && !wasFetchingJobs.current) {
            setFetchMinimised(false);
            const startedAt = Date.now();
            setFetchStartedAt(startedAt);
            sessionStorage.setItem(JOBS_FETCH_KEY, JSON.stringify({ startedAt }));
        }
        if (!isFetchingJobs && wasFetchingJobs.current) {
            sessionStorage.removeItem(JOBS_FETCH_KEY);
            setFetchStartedAt(null);
        }
        wasFetchingJobs.current = isFetchingJobs;
    }, [isFetchingJobs]);

    // Restore an interrupted fetch by polling the server's run record — the only
    // signal that distinguishes "still working" from "finished, added nothing".
    useEffect(() => {
        let marker;
        try {
            marker = JSON.parse(sessionStorage.getItem(JOBS_FETCH_KEY) || 'null');
        } catch {
            marker = null;
        }
        if (!marker?.startedAt) return;

        if (Date.now() - marker.startedAt > JOBS_FETCH_TTL_MS) {
            sessionStorage.removeItem(JOBS_FETCH_KEY);
            return;
        }

        let cancelled = false;
        let pollId = null;

        const finish = async (run) => {
            if (cancelled) return;
            if (pollId) clearInterval(pollId);
            sessionStorage.removeItem(JOBS_FETCH_KEY);
            setRestoredFetch(false);
            // Reload once at the end so the jobs the server added while this page was
            // away actually appear. Skipped when the fetch added nothing.
            if (run?.added > 0) {
                try {
                    await dispatch(fetchUserJobs()).unwrap();
                    // "Last fetched" is an explicit ID set, so a run that landed while
                    // this page was away has to hand its IDs over — refreshing the list
                    // alone leaves the chip showing the previous fetch's count.
                    // "Fetched today" is derived from addedAt and self-corrects.
                    markRecentlyFetched(run.addedJobIds || []);
                } catch { /* list refresh is best-effort */ }
            }
            if (run) {
                setToastMessage({
                    show: true,
                    message: run.status === 'failed'
                        ? (run.message || 'Job fetch failed. Please try again.')
                        : (run.added > 0
                            ? `✅ ${run.added} jobs added while you were away · Free — no credits used`
                            : (run.message || 'No new ATS jobs found right now.')),
                    type: run.status === 'failed' ? 'error' : (run.added > 0 ? 'success' : 'info')
                });
                setTimeout(() => setToastMessage(prev => ({ ...prev, show: false })), 5000);
            }
        };

        // The server's own record of the run. Unlike watching the list grow, this
        // reports a fetch that finished having added nothing.
        const check = async () => {
            if (Date.now() - marker.startedAt > JOBS_FETCH_TTL_MS) {
                await finish(null);
                return false;
            }
            const { data } = await api.get('/api/jobs/fetch-status');
            if (cancelled) return false;
            const run = data?.run;
            if (!run) {
                await finish(null);
                return false;
            }
            // Not every trigger of the loader is a tracked POST — a plain first-visit
            // list load is not. Without this guard that case reads a completed run
            // from some earlier fetch and reports its result as if it had just
            // happened.
            const runStarted = new Date(run.startedAt).getTime();
            if (!runStarted || runStarted < marker.startedAt - 15000) {
                await finish(null);
                return false;
            }
            if (run.status === 'running') return true;
            await finish(run);
            return false;
        };

        // Verify BEFORE showing anything, so a stale marker cannot flash a phantom
        // fetch on a page where nothing is running.
        (async () => {
            let running;
            try {
                running = await check();
            } catch {
                // Server unreachable: no basis to claim a fetch is running.
                return finish(null);
            }
            if (!running || cancelled) return;
            // Continue from the original start, so a reload does not reset progress.
            setFetchStartedAt(marker.startedAt);
            setRestoredFetch(true);
            setFetchMinimised(true);
            pollId = setInterval(() => {
                check().catch(() => { /* a failed poll says nothing; let the TTL decide */ });
            }, JOBS_FETCH_POLL_MS);
        })();

        return () => { cancelled = true; if (pollId) clearInterval(pollId); };
    }, [dispatch]);

    return (
        <div className="relative h-screen bg-[#FCFCFC] pt-16 flex flex-col">
            {/* Jobs · fetching loader (Claude Design handoff) */}
            <JobsFetchingLoader
                isLoading={(isFetchingJobs || restoredFetch) && !fetchMinimised}
                startedAt={fetchStartedAt}
                text={loadingMoreJobs || keywordFetchLoading || atsFetchLoading ? gamifiedMessages : "Fetching jobs for you…"}
                onMinimise={() => setFetchMinimised(true)}
            />
            <ScanProgressPill
                isVisible={(isFetchingJobs || restoredFetch) && fetchMinimised}
                title="Fetching jobs"
                message={restoredFetch ? 'Finishing in the background' : 'Pulling and scoring new roles'}
                onExpand={() => setFetchMinimised(false)}
                startedAt={fetchStartedAt}
            />
            
            {/* Loading Overlay for Saving Jobs */}
            <LoadingOverlay 
                isLoading={savingJobs} 
                text={saveJobsProgress}
            />
            
            {/* Jobs Loaded Notification */}
            <JobNotification 
                show={showJobNotification} 
                count={jobsLoadedCount}
                onClose={() => setShowJobNotification(false)} 
            />
            
            {showModal && (
                <OnboardingModal
                    isOpen={showModal}
                    handleCloseModal={handleCloseModal}
                />
            )}
            
            {showFetchJobsModal && (
                <FetchJobsModal
                    isOpen={showFetchJobsModal}
                    onClose={() => setShowFetchJobsModal(false)}
                    onFetchByPreference={handleFetchByPreference}
                    onFetchByKeyword={handleFetchByKeyword}
                    onFetchATSOnly={handleFetchATSOnly}
                    onFetchATSByKeyword={handleFetchATSByKeyword}
                    loading={localLoading}
                    creditsBalance={user?.creditsBalance || 0}
                />
            )}

            {!showModal && <Navbaar />}

            <div
                className={`flex flex-1 min-h-0 w-full transition-all duration-300 ${showModal ? 'blur-sm pointer-events-none select-none' : ''}`}>
                <div className="hidden sm:flex flex-col w-64 border-r border-gray-200 bg-white flex-shrink-0">
                    <Sidebar handleOpenSidebar={handleOpenSidebar} />
                </div>

                <div className="hidden">
                    <JobSidebar
                        selectedJobTypes={selectedJobTypes}
                        setSelectedJobTypes={setSelectedJobTypes}
                        minSalary={minSalary}
                        maxSalary={maxSalary}
                        setMinSalary={setMinSalary}
                        setMaxSalary={setMaxSalary}
                        selectedExperience={selectedExperience}
                        setSelectedExperience={setSelectedExperience}
                        handleOpenSidebar={handleOpenSidebar}
                        selectedCities={selectedCities}
                        setSelectedCities={setSelectedCities}
                        cityOptions={cityOptions}
                        clearFilters={clearFilters}
                    />
                </div>

                <div className="flex-1 flex flex-col mb-14 sm:mb-0 overflow-hidden relative">
                    <div className="flex-1 flex flex-col overflow-hidden px-4 pb-6">
                        {syncing && (
                            <div className="w-full p-3 mb-4 bg-blue-100 border border-blue-300 text-blue-800 rounded-lg text-center">
                                <p className="font-semibold animate-pulse">
                                    Syncing new jobs based on your profile... Please wait.
                                </p>
                            </div>
                        )}
                        
                        <div className="flex flex-col lg:flex-row gap-0 w-full h-full relative">
                            <div ref={centerJobsListRef} className="w-full overflow-y-auto hide-scrollbar mt-0 min-w-0 pr-0 sm:pr-2">
                                <div className="sticky top-0 z-20 bg-[#FCFCFC]">
                                    <div className="px-0 py-5 shrink-0">
                                        <JobHeader
                                            query={query}
                                            setQuery={setQuery}
                                            selectedJobTypes={selectedJobTypes}
                                            setSelectedJobTypes={setSelectedJobTypes}
                                            minSalary={minSalary}
                                            maxSalary={maxSalary}
                                            setMinSalary={setMinSalary}
                                            setMaxSalary={setMaxSalary}
                                            locationQuery={locationQuery}
                                            setLocationQuery={setLocationQuery}
                                            selectedExperience={selectedExperience}
                                            setSelectedExperience={setSelectedExperience}
                                            selectedCities={selectedCities}
                                            setSelectedCities={setSelectedCities}
                                            cityOptions={cityOptions}
                                            selectedScoreRanges={selectedScoreRanges}
                                            setSelectedScoreRanges={setSelectedScoreRanges}
                                            clearFilters={clearFilters}
                                            sortBy={sortBy}
                                            setSortBy={setSortBy}
                                        />
                                    </div>

                                    <div className="w-full flex flex-wrap justify-between relative bg-[#FCFCFC] z-10 px-0 py-4 shrink-0">
                                        <JobSubHeader
                                            searchResult={chipCounts[quickFilter] ?? displayJobs.length}
                                            selectedJobTypes={selectedJobTypes}
                                            minSalary={minSalary}
                                            maxSalary={maxSalary}
                                            handleSaveSelectedJobs={handleSaveSelectedJobs}
                                            selectedJobsLength={selectedJobs.length}
                                            localLoading={loadingMoreJobs}
                                            handleFetchMoreJobs={handleFetchMoreJobs}
                                        />
                                    </div>

                                    {/* Quick-filter chips + synced label */}
                                    <div className="flex items-center gap-2 pb-3 flex-wrap">
                                        {[
                                            { value: 'all', label: 'All' },
                                            // Persistent fetch filters (survive refresh, based on when jobs were added).
                                            { value: 'last-fetched', label: 'Last fetched' },
                                            { value: 'fetched-today', label: 'Fetched today' },
                                            { value: 'saved', label: 'Saved' },
                                            { value: 'unsaved', label: 'Unsaved' },
                                            { value: 'excellent', label: 'Excellent fit' },
                                            { value: 'referral', label: 'Has referral' },
                                            { value: 'remote', label: 'Remote' },
                                            { value: 'recent', label: 'Posted this week' },
                                        ].map((chip) => (
                                            <button
                                                key={chip.value}
                                                type="button"
                                                onClick={() => setQuickFilter(chip.value)}
                                                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] font-medium transition-colors border ${
                                                    quickFilter === chip.value
                                                        ? 'bg-[#3F57E0] text-white border-[#3F57E0]'
                                                        : 'bg-white text-[#334155] border-[#e2e8f0] hover:border-[#3F57E0] hover:text-[#3F57E0]'
                                                }`}
                                            >
                                                {chip.label}
                                                <span className={`text-[12px] font-semibold ${quickFilter === chip.value ? 'text-white/80' : 'text-gray-400'}`}>
                                                    {chipCounts[chip.value]}
                                                </span>
                                            </button>
                                        ))}
                                        <div className="flex-1" />
                                        {(jobsLoading || syncing || loadingMoreJobs || keywordFetchLoading || (effectiveUserId && !initialJobsFetched)) ? (
                                            <span className="text-xs text-[#3F57E0] flex-shrink-0 flex items-center gap-1.5">
                                                <span className="inline-block h-3 w-3 rounded-full border-2 border-[#3F57E0]/30 border-t-[#3F57E0] animate-spin" />
                                                Updating jobs…
                                            </span>
                                        ) : (
                                            <span className="text-xs text-gray-400 flex-shrink-0">
                                                {(() => {
                                                    if (!lastSyncedAt) return 'Synced just now';
                                                    const diff = Math.floor((Date.now() - new Date(lastSyncedAt).getTime()) / 60000);
                                                    if (diff < 1) return 'Synced just now';
                                                    if (diff === 1) return 'Synced 1 min ago';
                                                    if (diff < 60) return `Synced ${diff} min ago`;
                                                    return 'Synced a while ago';
                                                })()}
                                            </span>
                                        )}
                                    </div>

                                    {/* Non-blocking Recruiter Loading Banner - In sticky header */}
                                    {loadingJobId && (
                                        <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 rounded-lg shadow-md">
                                            <div className="flex items-center gap-3">
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 flex-shrink-0"></div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-semibold text-blue-900">
                                                        Finding recruiters{loadingCompanyName ? ` at ${loadingCompanyName}` : ''}...
                                                    </p>
                                                    <p className="text-xs text-blue-700 mt-0.5">
                                                        Searching for contacts. You can continue browsing!
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        console.log('[DEBUG] Dismissing recruiter loading banner');
                                                        setLoadingJobId(null);
                                                        setLoadingCompanyName(null);
                                                    }}
                                                    className="text-blue-600 hover:text-blue-800 text-2xl font-bold leading-none flex-shrink-0 w-6 h-6 flex items-center justify-center"
                                                    aria-label="Dismiss"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="flex flex-col gap-4">
                                    {!jobsLoading && !syncing && !loadingMoreJobs && !keywordFetchLoading && jobsError && displayJobs.length === 0 ? (
                                        <div className="w-full p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg">
                                            <p className="font-semibold">Couldn’t load jobs right now.</p>
                                            <p className="text-sm mt-1 opacity-90">{String(jobsError)}</p>
                                        </div>
                                    ) : displayJobs.length === 0 && (jobsLoading || syncing || loadingMoreJobs || keywordFetchLoading || (effectiveUserId && !initialJobsFetched)) ? (
                                        <>{[0, 1, 2, 3, 4].map(i => <JobCardSkeleton key={i} />)}</>
                                    ) : !jobsLoading && !syncing && !loadingMoreJobs && !keywordFetchLoading && displayJobs.length === 0 ? (
                                        <p>No jobs found. Try adjusting your filters or wait for new jobs to be synced.</p>
                                    ) : (
                                        paginatedDisplayJobs.map((job, index) => {
                                            const matchSignature = getJobMatchSignature(job);
                                            const encodedMatchSignature = encodeURIComponent(matchSignature);
                                            const isHighlighted = chatScrollHighlightKey === matchSignature;
                                            const isNew = isJobNew(job);
                                            return (
                                            <div
                                                key={job._id || index}
                                                data-job-scroll-key={encodedMatchSignature}
                                                className={isHighlighted ? 'rounded-xl ring-2 ring-[#3F57E0] ring-offset-2 ring-offset-[#FCFCFC] transition-all duration-300' : ''}
                                            >
                                                <JobCard
                                                    job={job}
                                                    isNew={isNew}
                                                    jobId={job._id}
                                                    applyLink={job.applyLink || '#'}
                                                    jobFitScore={job.companyId?.fitScore}
                                                    compensation={job?.companyId?.scores?.compensation?.weightedScore}
                                                    stability={job?.companyId?.scores?.stability?.weightedScore}
                                                    growth={job?.companyId?.scores?.careerGrowth?.weightedScore}
                                                    WLB={job?.companyId?.scores?.workLifeBalance?.weightedScore}
                                                    toxicity={job?.companyId?.scores?.toxicity?.weightedScore}
                                                    referral={job?.companyId?.scores?.referral?.weightedScore}
                                                    parameterKeys={job?.parameterKeys}
                                                    postedAt={
                                                        job.postedAt ||
                                                        job.job_posted_at_timestamp
                                                    }
                                                    handleCardClick={() =>
                                                        handleCardClick(job)
                                                    }
                                                    savedJobs={savedJobs}
                                                    selectedJobs={selectedJobs}
                                                    setSelectedJobs={setSelectedJobs}
                                                    toastMessage={toastMessage}
                                                    setToastMessage={setToastMessage}
                                                    handleSaveSelectedJobs={handleSaveSelectedJobs}
                                                    loadingJobId={loadingJobId}
                                                    setLoadingJobId={setLoadingJobId}
                                                    setLoadingCompanyName={setLoadingCompanyName}
                                                    onAddCompanyCard={handleAddOrUpdateCompanyCard}
                                                    onUpdateCompanyAnalysis={handleUpdateCompanyAnalysis}
                                                />
                                            </div>
                                            );
                                        })
                                    )}
                                </div>
                                
                                {/* Pagination Controls */}
                                {displayJobs.length > 0 && (
                                    <div className="flex items-center justify-between mt-6 pb-4 px-4 border-t pt-4">
                                        {/* Left: limit dropdown */}
                                        <div className="relative inline-flex items-center">
                                            <select
                                                value={jobsPerPage}
                                                onChange={(e) => { setJobsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                                                className="appearance-none pl-3 pr-7 py-2 text-[12px] font-semibold text-[#3F57E0] bg-white border-2 border-[#3F57E0] rounded-lg shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#3F57E0]/20 hover:bg-[#EEF1FE] transition-colors"
                                            >
                                                <option value={10}>10 / page</option>
                                                <option value={20}>20 / page</option>
                                                <option value={50}>50 / page</option>
                                                <option value={100}>100 / page</option>
                                            </select>
                                            <svg className="pointer-events-none absolute right-2 w-3.5 h-3.5 text-[#3F57E0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>

                                        {/* Right: count + page nav */}
                                        <div className="flex items-center gap-3">
                                            <div className="text-sm text-gray-600">
                                                Showing {Math.min((currentPage - 1) * jobsPerPage + 1, displayJobs.length)} to {Math.min(currentPage * jobsPerPage, displayJobs.length)} of {displayJobs.length} jobs
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                    disabled={currentPage === 1}
                                                    className={`px-4 py-2 rounded-lg border transition-colors ${
                                                        currentPage === 1
                                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                            : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                                                    }`}
                                                >
                                                    Previous
                                                </button>

                                                <div className="flex items-center gap-1">
                                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                        let pageNumber;
                                                        if (totalPages <= 5) {
                                                            pageNumber = i + 1;
                                                        } else if (currentPage <= 3) {
                                                            pageNumber = i + 1;
                                                        } else if (currentPage >= totalPages - 2) {
                                                            pageNumber = totalPages - 4 + i;
                                                        } else {
                                                            pageNumber = currentPage - 2 + i;
                                                        }
                                                        return (
                                                            <button
                                                                key={i}
                                                                onClick={() => setCurrentPage(pageNumber)}
                                                                className={`w-10 h-10 rounded-lg transition-colors ${
                                                                    currentPage === pageNumber
                                                                        ? 'bg-blue-600 text-white'
                                                                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                                                                }`}
                                                            >
                                                                {pageNumber}
                                                            </button>
                                                        );
                                                    })}
                                                </div>

                                                <button
                                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                                    disabled={currentPage === totalPages}
                                                    className={`px-4 py-2 rounded-lg border transition-colors ${
                                                        currentPage === totalPages
                                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                            : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                                                    }`}
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Right panel: Company Cards with Tabs — hidden in v2 layout (recruiter info is now inline in expanded card) */}
                            <div className="hidden">
                                <ErrorBoundary
                                    name="Referral analysis panel"
                                    fallback={
                                        <div className="p-4 text-sm text-gray-600">
                                            Referral analysis is temporarily unavailable. Your jobs list is still available.
                                        </div>
                                    }
                                >
                                    <RightPanelTabs
                                        companies={fetchedCompanies}
                                        onViewCompany={handleViewCompany}
                                        onDeleteCompany={handleDeleteCompanyCard}
                                        activeTab={activeRightTab}
                                        onTabChange={setActiveRightTab}
                                        selectedCompany={selectedCompany}
                                        onCloseCompany={() => setSelectedCompany(null)}
                                    />
                                </ErrorBoundary>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {toastMessage.show && (
                <div
                    className={`fixed bottom-4 left-1/2 transform -translate-x-1/2 text-white px-4 py-2 rounded shadow-lg transition-opacity duration-500 
            ${toastMessage.type === 'error' ? 'bg-amber-500' : toastMessage.type === 'info' ? 'bg-blue-600' : 'bg-emerald-600'}`
                    }
                >
                    {toastMessage.message}
                </div>
            )}
            
            {/* Recruiter Modal */}
            {isRecruiterModalOpen && activeCompanyModal && (
                <RecruiterModal
                    data={activeCompanyModal.recruiterData}
                    job={activeCompanyModal.jobData}
                    onClose={handleCloseRecruiterModal}
                    handleSaveSelectedJobs={handleSaveSelectedJobs}
                    analysisAlreadyDone={!!activeCompanyModal.analyzedData}
                    preAnalyzedData={activeCompanyModal.analyzedData}
                    onAnalysisComplete={(analyzedData) => {
                        handleUpdateCompanyAnalysis(activeCompanyModal.jobId, analyzedData, { analysisComplete: true });
                    }}
                />
            )}
        </div>
    );
};

export default Jobs;